/**
 * Website Domain Seed Data
 *
 * Contains seed data for the Website Portal (36):
 * - WebsiteDomainMappings (seeded in Tenant Service 25)
 * - SiteConfig, FundedDeals, Testimonials, BlogPosts (seeded in Tenant Service 25)
 *
 * All content is for the "Coastal Real Estate Funding" demo brand, which is the
 * public-facing website identity of OpsMatrix Capital (BUSINESS_IDS.OPSMATRIX_CAPITAL).
 *
 * Content is synthetic demo data inspired by real estate lending patterns.
 */

import { type BrandConfig, type WebsiteCmsV4, WebsiteCmsV4Schema } from '../../schemas/website-cms';
import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// SEED IDs
// =============================================================================

export const WEBSITE_SEED_IDS = {
  /** Domain mapping for demo slug */
  DOMAIN_MAPPING_DEMO: seedId('website-domain', 1),
  /** SiteConfig for OpsMatrix Capital */
  SITE_CONFIG_COASTAL: seedId('website-config', 1),
  /** Funded deals */
  FUNDED_DEAL_1: seedId('website-deal', 1),
  FUNDED_DEAL_2: seedId('website-deal', 2),
  FUNDED_DEAL_3: seedId('website-deal', 3),
  FUNDED_DEAL_4: seedId('website-deal', 4),
  FUNDED_DEAL_5: seedId('website-deal', 5),
  /** Testimonials */
  TESTIMONIAL_1: seedId('website-testimonial', 1),
  TESTIMONIAL_2: seedId('website-testimonial', 2),
  TESTIMONIAL_3: seedId('website-testimonial', 3),
  TESTIMONIAL_4: seedId('website-testimonial', 4),
  /** Blog posts */
  BLOG_POST_1: seedId('website-blog', 1),
  BLOG_POST_2: seedId('website-blog', 2),
  BLOG_POST_3: seedId('website-blog', 3),
} as const;

// =============================================================================
// DOMAIN MAPPING (seeded in Tenant Service)
// =============================================================================

export interface WebsiteDomainMappingSeed {
  id: string;
  businessId: string;
  slug: string;
  domain: string;
  verified: boolean;
  sslStatus: string;
}

export const DEMO_DOMAIN_MAPPING: WebsiteDomainMappingSeed = {
  id: WEBSITE_SEED_IDS.DOMAIN_MAPPING_DEMO,
  businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
  slug: 'demo',
  domain: 'demo.website.opsmatrix.dev', // Platform subdomain (no custom domain yet)
  verified: true,
  sslStatus: 'ACTIVE',
};

// =============================================================================
// SITE CONFIG (seeded in CRM Service)
// =============================================================================

/**
 * Full SiteConfig for the Coastal Real Estate Funding demo website.
 *
 * Content sourced from coastal-funding.com. Real team bios, real loan programs,
 * real geographic focus (Florida). Designed to match modern private lending UX
 * patterns (Kiavi, Easy Street Capital, Lima One, RCN Capital).
 *
 * 8 pages: Home, About, Services, Leadership, Geographic, Funded Deals, Contact
 * + Privacy Policy, Terms of Service
 */
export const COASTAL_FUNDING_SITE_CONFIG = {
  published: true,
  templateId: 'full-service',
  schemaVersion: 1,

  announcementBar: {
    enabled: true,
    badge: 'New Rate',
    message: 'DSCR rates now starting at',
    linkText: '6.00% — Get Pre-Qualified',
    linkHref: '/apply',
    dismissible: true,
  },

  branding: {
    companyName: 'Coastal Real Estate Funding',
    logo: '/assets/customers/coastal-funding/logo.png',
    logoAlt: 'Coastal Real Estate Funding Logo',
    favicon: '/assets/customers/coastal-funding/favicon.jpg',
    primaryColor: '#043B64',
    secondaryColor: '#FFFFFF',
    accentColor: '#043B64',
    fontFamily: 'Inter',
    headingFontFamily: 'Inter',
    ogImage: '/assets/customers/coastal-funding/hero-bg.jpg',
    themeMode: 'light',
  },

  nav: [
    { label: 'Home', href: '/' },
    {
      label: 'Services',
      href: '/services',
      children: [
        { label: 'Fix & Flip Loans', href: '/services#fix-flip' },
        { label: 'Ground-Up Construction', href: '/services#construction' },
        { label: 'Bridge Loans', href: '/services#bridge' },
        { label: 'DSCR Bridge Plus', href: '/services#dscr-bridge-plus' },
      ],
    },
    {
      label: 'Tools',
      href: '#',
      children: [
        { label: 'Fix & Flip Calculator', href: '/calculators/fix-and-flip' },
        { label: 'DSCR Calculator', href: '/calculators/dscr' },
        { label: 'How It Works', href: '/process' },
        { label: 'Glossary', href: '/resources/glossary' },
      ],
    },
    {
      label: 'About',
      href: '/about',
      children: [
        { label: 'Our Story', href: '/about' },
        { label: 'Leadership Team', href: '/leadership' },
        { label: 'Geographic Focus', href: '/geographic' },
      ],
    },
  ],

  ctaButton: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },

  // Site-wide navigation config with mega-menu dropdowns
  navigation: {
    type: 'navigation',
    variant: 'transparent',
    items: [
      { label: 'Home', href: '/' },
      {
        label: 'Products',
        href: '/services',
        children: [
          {
            label: 'Fix & Flip',
            href: '/services#fix-flip',
            description: 'Short-term capital for property renovation and resale',
            category: 'Fix-and-Flip',
          },
          {
            label: 'Bridge Loans',
            href: '/services#bridge',
            description: 'Flexible short-term financing for acquisitions and refinances',
            category: 'Fix-and-Flip',
          },
          {
            label: 'DSCR Rental',
            href: '/services#dscr-bridge-plus',
            description: 'Cash-flow-based financing for rental investors',
            category: 'Rental',
          },
          {
            label: 'Ground-Up Construction',
            href: '/services#construction',
            description: 'Staged financing for spec builds and development',
            category: 'New Construction',
          },
          {
            label: '',
            href: '#',
            featured: {
              image: '/assets/customers/coastal-funding/hero-bg.jpg',
              title: 'Get Pre-Qualified',
              description: 'Fast pre-qualification in 24 hours. No commitment, no hard credit pull.',
              href: '/apply',
            },
          },
        ],
      },
      {
        label: 'Tools',
        href: '#',
        children: [
          {
            label: 'Fix & Flip Calculator',
            href: '/calculators/fix-and-flip',
            description: 'Estimate your profit on a fix & flip deal',
            category: 'Calculators',
          },
          {
            label: 'DSCR Calculator',
            href: '/calculators/dscr',
            description: 'Check if your rental property qualifies',
            category: 'Calculators',
          },
          {
            label: 'How It Works',
            href: '/process',
            description: 'Our simple 4-step funding process',
            category: 'Resources',
          },
          {
            label: 'Glossary',
            href: '/resources/glossary',
            description: 'Real estate lending terms explained',
            category: 'Resources',
          },
          {
            label: '',
            href: '#',
            featured: {
              image: '/assets/customers/coastal-funding/hero-bg.jpg',
              title: 'Get Pre-Qualified',
              description: 'Fast pre-qualification in 24 hours. No commitment, no hard credit pull.',
              href: '/apply',
            },
          },
        ],
      },
      {
        label: 'About',
        href: '/about',
        children: [
          { label: 'Our Story', href: '/about', description: 'Built by investors, for investors', category: 'Company' },
          {
            label: 'Leadership Team',
            href: '/leadership',
            description: 'Meet the team behind Coastal',
            category: 'Company',
          },
          {
            label: 'Geographic Focus',
            href: '/geographic',
            description: 'Florida markets we serve',
            category: 'Markets',
          },
          { label: 'Contact Us', href: '/contact', description: 'Get in touch with our team', category: 'Markets' },
        ],
      },
      // Top-level Partners link → /partners landing page (added 2026-04-29 to
      // match production; the `pages.partner` entry below renders this route).
      { label: 'Partners', href: '/partners' },
    ],
    sticky: true,
    showPhone: true,
    phone: '(239) 259-9829',
    ctaButton: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
  },

  // Site-wide default footer (rendered on every page automatically)
  footer: {
    type: 'footer',
    variant: 'expanded',
    columns: [
      {
        heading: 'Quick Links',
        links: [
          { label: 'Home', href: '/' },
          { label: 'Services', href: '/services' },
          { label: 'About', href: '/about' },
          { label: 'Leadership', href: '/leadership' },
          { label: 'Geographic Focus', href: '/geographic' },
        ],
      },
      {
        heading: 'Services',
        links: [
          { label: 'Fix & Flip Loans', href: '/services#fix-flip' },
          { label: 'Ground-Up Construction', href: '/services#construction' },
          { label: 'Bridge Loans', href: '/services#bridge' },
          { label: 'DSCR Bridge Plus', href: '/services#dscr-bridge-plus' },
        ],
      },
      {
        heading: 'Tools',
        links: [
          { label: 'DSCR Calculator', href: '/calculators/dscr' },
          { label: 'Fix & Flip Calculator', href: '/calculators/fix-and-flip' },
          { label: 'Pre-Qualification', href: '/apply' },
        ],
      },
      {
        heading: 'Contact',
        links: [
          { label: '(239) 259-9829', href: 'tel:+12392599829' },
          { label: 'dave@coastal-funding.com', href: 'mailto:dave@coastal-funding.com' },
          { label: 'Get Pre-Qualified', href: '/apply' },
        ],
      },
    ],
    tagline: 'Trusted private lending partner, providing direct and reliable financing for real estate investors.',
    address: '8191 College Pkwy\nSuite 302\nFort Myers, FL 33919',
    phoneNumbers: [{ label: 'Main Office', number: '(239) 259-9829' }],
    showEqualHousing: false,
    showNmls: false,
    socialLinks: [
      {
        platform: 'linkedin',
        url: 'https://www.linkedin.com/company/coastal-real-estate-funding',
        ariaLabel: 'Follow us on LinkedIn',
      },
    ],
    legalLinks: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
    ],
    disclaimer:
      'Coastal Real Estate Funding LLC | Business Purpose Lending Only | Governed by Florida Law. This is not a commitment to lend. Programs, rates, terms, and conditions are subject to change without notice. All loans are subject to underwriting approval.',
    padding: 'medium',
  },

  portalLinks: {
    applyNow: '/apply',
    borrowerLogin: 'https://app.coastal-funding.com',
    partnerPortal: 'https://portal.coastal-funding.com',
  },

  seo: {
    title: '%s | Coastal Real Estate Funding - Private Lending for Florida Investors',
    description:
      'Fast, reliable private lending for Florida real estate investors. Fix & flip, ground-up construction, bridge loans, and DSCR Bridge Plus. Close in as few as 5 days.',
    ogType: 'website',
  },

  pages: {
    // =========================================================================
    // HOME PAGE
    // =========================================================================
    home: {
      slug: '',
      title: 'Home',
      layout: 'full-width',
      sections: [
        // Navigation: uses site-level nav config (no per-page override needed)

        // --- Hero ---
        {
          type: 'hero',
          variant: 'split-screen',
          headline: 'Fund Your Next Deal in 5–10 Days',
          subheadline:
            'Direct capital from a team that has built, renovated, and financed over 36,000 units. No broker chains. No surprises. Fix & flip, construction, bridge, and DSCR loans across Florida.',
          backgroundImage: '/assets/customers/coastal-funding/hero-bg.jpg',
          overlayOpacity: 0.6,
          ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
          ctaSecondary: { label: 'View Loan Programs', href: '/services', variant: 'outline' },
          socialProof: {
            rating: 4.9,
            ratingLabel: 'Exceptional: 4.9 out of 5 Stars',
            reviewCount: 127,
          },
          trustPanel: [
            { value: '24h', label: 'Pre-Qualification Turnaround' },
            { value: '4', label: 'Loan Programs Available' },
            { value: '5', label: 'Florida Regions Covered' },
            { value: '✓', label: 'Direct Lender — No Brokers' },
          ],
          trustBadges: [],
          padding: 'large',
        },

        // --- Stats Counter ---
        {
          type: 'stats-counter',
          heading: 'Built on Real-World Experience',
          stats: [
            {
              value: 20,
              suffix: '+',
              label: 'Years Experience',
              description: 'of hands-on real estate finance and investment expertise across Florida',
            },
            {
              value: 1200,
              suffix: '+',
              label: 'Projects Completed',
              description: 'renovation and construction projects successfully funded and closed',
            },
            {
              value: 4000,
              suffix: '+',
              label: 'Lots Transacted',
              description: 'residential lots acquired, developed, and sold across the Sunshine State',
            },
            {
              value: 5,
              label: 'Florida Regions',
              description: 'covered statewide — Southwest, Southeast, Central, Tampa Bay, and North Florida',
            },
          ],
          layout: 'row',
          animateOnScroll: true,
          animationDurationMs: 2500,
          padding: 'medium',
          background: 'rgba(255,255,255,0.04)',
        },

        // --- Loan Programs (Grid) ---
        {
          type: 'loan-programs',
          heading: 'Lending Solutions',
          subheading:
            'Pragmatic underwriting. Direct funding. Long-term relationships. Market leading loan structures for every scenario.',
          layout: 'grid',
          columns: 2,
          programs: [
            {
              id: 'fix-flip',
              name: 'Fix & Flip Loans',
              shortDescription:
                'Short-term, asset-based capital for acquiring and renovating residential properties for resale. Designed for active investors who value speed, leverage, and predictable execution.',
              slug: 'fix-and-flip',
              highlights: [
                'Starting at 8.49%',
                'Up to 100% of Purchase Price',
                'Up to 100% of Rehab Budget',
                '12-24 Month Terms',
                'Close in 5-10 Days',
                'No Prepayment Penalty',
                '72-Hour Draw Funding',
              ],
              rateRange: 'Starting at 8.49%',
              maxLtv: 100,
              termMonths: [12, 18, 24],
              loanRange: '$100K-$5M',
            },
            {
              id: 'construction',
              name: 'Ground-Up Construction',
              shortDescription:
                'Staged construction financing for spec builds and investor-driven development projects. Structured around real-world builder timelines and milestone-based draw schedules.',
              slug: 'construction',
              highlights: [
                'Starting at 8.99%',
                'Up to 90% Loan-to-Total-Cost',
                'Up to 85% ARV',
                '$250K-$5M Loan Size',
                '12-24 Month Terms',
                '72-Hour Draw Funding',
                'Substitute Builder Experience for Borrower',
              ],
              rateRange: 'Starting at 8.99%',
              maxLtv: 90,
              termMonths: [12, 18, 24],
            },
            {
              id: 'bridge',
              name: 'Bridge Loans',
              shortDescription:
                'Flexible short-term capital for acquisitions, refinances, cash-outs, and transitional properties. Ideal for investors executing time-sensitive opportunities.',
              slug: 'bridge',
              highlights: [
                'Starting at 8.75%',
                'Up to 80% LTV (Purchase)',
                'Up to 75% LTV (Refinance)',
                '6-24 Month Terms',
                '$100K-$5M Loan Size',
                'No prepayment penalty',
              ],
              rateRange: 'Starting at 8.75%',
              maxLtv: 80,
              termMonths: [6, 12, 24],
            },
            {
              id: 'dscr-bridge-plus',
              name: 'DSCR / Rental',
              shortDescription:
                'Cash-flow-based financing for rental investors. Qualify based on property income — no personal income verification required.',
              slug: 'dscr-bridge-plus',
              highlights: [
                'Starting at 6.00%',
                'Up to 80% LTV',
                'DSCR ≥ 1.0x',
                'Up to 10 Years IO',
                'Varied Prepayment Penalty Options',
                '$100K-$3M Loan Size',
              ],
              rateRange: 'Starting at 6.00%',
              maxLtv: 80,
              termMonths: [12, 24, 60, 120],
            },
          ],
          showViewAll: true,
          viewAllBasePath: '/services',
          padding: 'large',
        },

        // --- Why Coastal Funding (icon grid — all visible, no accordion) ---
        {
          type: 'faq',
          variant: 'icon-grid',
          heading: 'Capital That Thinks Like an Investor',
          subheading:
            'We are builders and investors who built a lending platform after years of borrowing ourselves. We structured Coastal to eliminate the friction, unpredictability, and unnecessary bureaucracy common in private lending.',
          items: [
            {
              question: 'Direct Institutional Capital',
              answer: 'No broker chains or capital stacking. We fund directly from our balance sheet.',
              icon: 'AccountBalance',
              category: 'Why Us',
            },
            {
              question: 'Operator-Built Underwriting',
              answer:
                'We understand construction risk because we have built, renovated, and operated properties ourselves.',
              icon: 'Construction',
              category: 'Why Us',
            },
            {
              question: 'Predictable Draw Process',
              answer:
                'Fast inspections and reliable funding. 72-hour draw turnaround so your project stays on schedule.',
              icon: 'Speed',
              category: 'Why Us',
            },
            {
              question: 'Transparent Pricing',
              answer: 'No hidden fees. Clear term sheets with upfront pricing from day one.',
              icon: 'Visibility',
              category: 'Why Us',
            },
            {
              question: 'Long-Term Relationships',
              answer:
                'We support repeat operators with better terms and a streamlined process on every subsequent deal.',
              icon: 'Handshake',
              category: 'Why Us',
            },
          ],
          allowMultipleOpen: true,
          padding: 'large',
          background: 'rgba(255,255,255,0.04)',
        },

        // --- How It Works (MOVED ABOVE testimonials + FAQ per feedback) ---
        {
          type: 'process-timeline',
          heading: 'How It Works',
          subheading: 'From first call to funded deal — a streamlined process designed for speed.',
          steps: [
            {
              number: 1,
              title: 'Submit Your Deal',
              description:
                'Tell us about the property, your experience, and your strategy using our quick online form.',
              icon: 'Description',
              duration: '5 minutes',
            },
            {
              number: 2,
              title: 'Get a Term Sheet',
              description: 'A dedicated loan officer reviews your deal and delivers a term sheet within 24 hours.',
              icon: 'Assignment',
              duration: '24 hours',
            },
            {
              number: 3,
              title: 'Underwriting & Diligence',
              description: 'We complete valuation, title, and borrower verification — fast and without surprises.',
              icon: 'FactCheck',
              duration: '2-5 days',
            },
            {
              number: 4,
              title: 'Close & Fund',
              description:
                'Sign documents and receive your wire. We coordinate with title and escrow for a seamless close.',
              icon: 'AccountBalance',
              duration: 'Same-day wire',
            },
          ],
          cta: { label: 'Start Your Application', href: '/apply', variant: 'primary' },
          padding: 'large',
          background: 'rgba(10,37,64,0.25)',
        },

        // --- Testimonials (Carousel) ---
        {
          type: 'testimonials',
          variant: 'carousel',
          heading: 'Trusted by Active Investors',
          subheading: 'What borrowers value most about working with Coastal.',
          testimonials: [
            {
              quote:
                'Coastal closed my first three deals in under 10 days each. Their team understood my renovation plan and got it done.',
              author: 'Marcus R.',
              authorTitle: 'Fix & Flip Investor',
              rating: 5,
              location: 'Miami, FL',
              platform: 'google',
              reviewDate: '11/15/25',
            },
            {
              quote:
                'The draw process on my ground-up build was seamless. Inspections were quick and funds hit my account within 48 hours.',
              author: 'Jennifer O.',
              authorTitle: 'Developer',
              rating: 5,
              location: 'Tampa, FL',
              platform: 'trustpilot',
              reviewDate: '10/22/25',
            },
            {
              quote:
                'When my conventional lender fell through two weeks before closing, Coastal stepped in and funded in 5 days.',
              author: 'David C.',
              authorTitle: 'Real Estate Investor',
              rating: 5,
              location: 'Fort Lauderdale, FL',
              platform: 'google',
              reviewDate: '09/03/25',
            },
            {
              quote:
                'As a DSCR borrower, I appreciated not having to provide income documentation. Coastal made the process painless.',
              author: 'Rachel T.',
              authorTitle: 'Portfolio Investor',
              rating: 5,
              location: 'Orlando, FL',
              platform: 'zillow',
              reviewDate: '08/18/25',
            },
          ],
          showRatings: true,
          padding: 'large',
          background: 'rgba(255,255,255,0.04)',
        },

        // --- FAQ (capped at 4 highest-friction questions) ---
        {
          type: 'faq',
          variant: 'two-column',
          heading: 'Frequently Asked Questions',
          items: [
            {
              question: 'What credit score do I need?',
              answer:
                'Most programs require a minimum 660 FICO. We focus on deal quality, borrower experience, and exit strategy — not just credit score. Strong deals with experienced borrowers may qualify with lower scores.',
              category: 'Qualification',
            },
            {
              question: 'How fast can you actually close?',
              answer:
                'We have closed in as few as 3 business days. Typical closings are 5-10 days for bridge and fix & flip, 10-14 days for construction, and 14-21 days for DSCR. Speed depends on clear title, appraisal, and borrower responsiveness.',
              category: 'Process',
            },
            {
              question: 'Are there prepayment penalties?',
              answer:
                'No prepayment penalties on fix & flip or bridge loans. DSCR loans may have a step-down prepayment structure (typically 3-2-1 or 5-4-3-2-1) depending on the term.',
              category: 'Terms',
            },
            {
              question: 'Do you work with first-time investors?',
              answer:
                'Yes. While experienced borrowers get the best terms, we actively work with first-time investors on fix & flip and bridge programs. We look for strong deals, adequate reserves, and a realistic plan.',
              category: 'Qualification',
            },
          ],
          allowMultipleOpen: false,
          padding: 'large',
        },

        // --- Pre-Qual Wizard (after FAQ — high-intent users have answers, now convert) ---
        {
          type: 'pre-qual-wizard',
          heading: 'Get Pre-Qualified in Minutes',
          subheading:
            'Answer a few project questions and our lending team will reach out with next-step guidance tailored to your scenario.',
          loanTypes: [
            {
              id: 'fix-flip',
              label: 'Fix & Flip',
              description: 'Purchase, renovate, and resell residential properties',
            },
            {
              id: 'construction',
              label: 'Ground-Up Construction',
              description: 'Build new residential or commercial properties',
            },
            { id: 'bridge', label: 'Bridge Loan', description: 'Short-term financing for acquisitions or refinances' },
            {
              id: 'dscr',
              label: 'DSCR / Rental',
              description: 'Investment property loans based on rental income',
            },
          ],
          padding: 'large',
        },

        // --- CTA Banner (final — varied label + urgency copy) ---
        {
          type: 'cta-banner',
          variant: 'full-width',
          headline: 'Talk to a Lending Specialist',
          body: 'Pre-qualification in 24 hours. Term sheet in 48 hours. No commitment, no hard credit pull.',
          ctaPrimary: { label: 'Start Your Application', href: '/apply', variant: 'primary' },
          ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
          showDecoration: true,
          padding: 'large',
        },

        // Footer is rendered automatically from site-level config
      ],
    },

    // =========================================================================
    // ABOUT PAGE — "Our Story" (team bios live on /leadership)
    // =========================================================================
    about: {
      slug: 'about',
      title: 'Our Story',
      sections: [
        // --- Hero ---
        {
          type: 'hero',
          variant: 'split-screen',
          headline: 'Built by Investors, for Investors',
          subheadline:
            'Coastal Real Estate Funding combines institutional capital markets experience with hands-on real estate operations to deliver fast, transparent private lending across Florida.',
          backgroundImage: '/assets/customers/coastal-funding/about-hero.jpg',
          ctaPrimary: { label: 'Meet the Team', href: '/leadership', variant: 'primary' },
          padding: 'large',
        },

        // --- Stats Counter ---
        {
          type: 'stats-counter',
          stats: [
            { value: 20, suffix: '+', label: 'Years Experience' },
            { value: 36000, suffix: '+', label: 'Units Financed' },
            { value: 300, suffix: '+', label: 'New Homes Built' },
            { value: 1200, suffix: '+', label: 'Renovations Completed' },
          ],
          layout: 'row',
          animateOnScroll: true,
          animationDurationMs: 2500,
          padding: 'medium',
          background: 'rgba(255,255,255,0.04)',
        },

        // --- Our Story (FAQ accordion) ---
        {
          type: 'faq',
          variant: 'two-column',
          heading: 'Our Story',
          subheading: 'How institutional experience and real-world operations come together to build a better lender',
          items: [
            {
              question: 'How We Started',
              answer:
                'Coastal Real Estate Funding was founded by David Diaz and Roger Lehman — two professionals who spent their careers on opposite sides of the same industry. David built and renovated thousands of homes across Florida while managing institutional portfolios. Roger structured and financed real estate deals from Wall Street trading desks and capital markets platforms. They saw the same problem from different angles: private lending was too slow, too opaque, and run by people who had never swung a hammer or walked a job site. They started Coastal to fix that.',
              category: 'Story',
            },
            {
              question: 'Our Mission',
              answer:
                'To deliver fast, transparent private lending built on institutional discipline and operator-level understanding. We exist to give real estate investors a capital partner who actually understands their business — not just the spreadsheets, but the properties, the timelines, and the risks that matter on the ground.',
              category: 'Story',
            },
            {
              question: 'What Sets Us Apart',
              answer:
                "Most private lenders come from either finance or operations. We come from both. Our team has underwritten more than $2 billion in structured real estate debt, managed institutional portfolios of 36,000+ units, and personally completed over 1,200 renovations. That dual perspective means we underwrite deals the way an investor evaluates them — looking at the property, the market, the exit strategy, and the borrower's ability to execute. The result: faster decisions, better terms, and a lending partner who speaks your language.",
              category: 'Story',
            },
            {
              question: 'Why Florida',
              answer:
                'Florida is where we live, invest, and build. We know the submarkets, the contractors, the permit timelines, and the neighborhood dynamics that national lenders miss. Our geographic focus means we can evaluate properties and markets with firsthand knowledge — not just data. That local expertise translates into faster underwriting, more accurate valuations, and better outcomes for borrowers.',
              category: 'Story',
            },
          ],
          allowMultipleOpen: true,
          padding: 'large',
        },

        // --- Our Approach (Process Timeline — with numbers + durations) ---
        {
          type: 'process-timeline',
          heading: 'Our Approach',
          subheading: 'Institutional discipline, investor-level speed',
          steps: [
            {
              number: 1,
              title: 'You Apply',
              description:
                'Tell us about your deal using our quick online form or call us directly. No commitment, no hard credit pull.',
              icon: 'Description',
              duration: '5 minutes',
            },
            {
              number: 2,
              title: 'We Evaluate',
              description:
                'We underwrite your deal the way an experienced investor would — property, market, scope, and exit strategy.',
              icon: 'FactCheck',
              duration: '24 hours',
            },
            {
              number: 3,
              title: 'You Get an Answer',
              description:
                'Pre-qualification within 24 hours. Term sheet within 48 hours. No bait-and-switch, no surprises.',
              icon: 'Assignment',
              duration: '48 hours',
            },
            {
              number: 4,
              title: 'We Fund',
              description:
                'Close in as few as 10 days. Our team is available for draw requests, questions, and guidance throughout.',
              icon: 'AccountBalance',
              duration: '5-10 days',
            },
          ],
          cta: { label: 'Start Your Application', href: '/apply', variant: 'primary' },
          padding: 'large',
        },

        // --- CTA Banner ---
        {
          type: 'cta-banner',
          variant: 'inset-card',
          headline: 'See How We Work',
          body: 'From first call to funded deal — a streamlined process designed for investors who value speed, transparency, and a team that understands the business.',
          ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
          ctaSecondary: {
            label: 'View Our Process',
            href: '/process',
            variant: 'outline',
          },
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // SERVICES PAGE (was Programs)
    // =========================================================================
    programs: {
      slug: 'services',
      title: 'Lending Services',
      sections: [
        // --- Hero ---
        {
          type: 'hero',
          variant: 'split-screen',
          headline: 'Lending Solutions Built by Operators',
          subheadline:
            'Every program is structured around how real investors actually work — fast closings, predictable draws, and terms that match your timeline.',
          backgroundImage: '/assets/customers/coastal-funding/services-hero.jpg',
          ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
          ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
          padding: 'large',
        },

        // --- Loan Programs (List - detail-rich with eligibility + benefits) ---
        {
          type: 'loan-programs',
          heading: 'Our Lending Solutions',
          subheading:
            'Each program is designed for a specific investment strategy. Not sure which fits? Call us at (239) 259-9829.',
          layout: 'list',
          columns: 2,
          programs: [
            {
              id: 'fix-flip',
              name: 'Fix & Flip Loans',
              shortDescription:
                'Fast funding for property renovation and resale. Our Fix & Flip loans provide quick and flexible financing for investors looking to purchase, renovate, and resell residential properties for profit. We understand the time-sensitive nature of these projects and offer streamlined approval processes to ensure you can seize opportunities swiftly.',
              slug: 'fix-and-flip',
              image: '/assets/customers/coastal-funding/programs/fix-and-flip.jpg',
              highlights: [
                'Up to 100% of Purchase Price + 100% of Rehab Budget',
                'Starting at 8.49%, 12-24 month terms',
                'Interest-only payments, close in 5-10 days',
                'No prepayment penalty, 72-hour draw funding',
              ],
              rateRange: 'Starting at 8.49%',
              maxLtv: 100,
              termMonths: [12, 18, 24],
              cta: { label: 'Pre-Qualify for Fix & Flip', href: '/apply', loanType: 'fix-flip' },
            },
            {
              id: 'construction',
              name: 'Ground-Up Construction Loans',
              shortDescription:
                'Financing for new residential and commercial construction. Designed for experienced developers, our Ground-Up Construction loans fund the entire building process from land acquisition to vertical construction. We partner with you to bring new properties to market, offering draw-based funding aligned with project milestones.',
              slug: 'construction',
              image: '/assets/customers/coastal-funding/programs/construction.jpg',
              highlights: [
                'Up to 90% Loan-to-Total-Cost, up to 85% ARV',
                'Starting at 8.99%, $250K-$5M loan size',
                '72-hour draw funding after inspection',
                'Substitute builder experience for borrower',
              ],
              rateRange: 'Starting at 8.99%',
              maxLtv: 90,
              termMonths: [12, 18, 24],
              cta: { label: 'Pre-Qualify for Construction', href: '/apply', loanType: 'construction' },
            },
            {
              id: 'bridge',
              name: 'Bridge Loans',
              shortDescription:
                'Short-term financing to bridge gaps in real estate transactions. Our Bridge Loans offer a rapid, short-term financing solution for real estate investors and developers who need to quickly acquire property, refinance existing debt, or access capital while awaiting long-term financing or property sale. Ideal for time-sensitive opportunities.',
              slug: 'bridge',
              image: '/assets/customers/coastal-funding/programs/bridge.jpg',
              highlights: [
                'Up to 80% LTV (purchase), up to 75% LTV (refinance)',
                'Starting at 8.75%, 6-24 month terms',
                'Interest-only payments, $100K-$5M loan size',
                'No prepayment penalty, close in 5-10 days',
              ],
              rateRange: 'Starting at 8.75%',
              maxLtv: 80,
              termMonths: [6, 12, 24],
              cta: { label: 'Pre-Qualify for Bridge', href: '/apply', loanType: 'bridge' },
            },
            {
              id: 'dscr-bridge-plus',
              name: 'DSCR Bridge Plus',
              shortDescription:
                "Debt Service Coverage Ratio (DSCR) based loans for investment properties. The DSCR Bridge Plus program is tailored for investors seeking financing for income-producing properties, where eligibility is primarily based on the property's cash flow rather than the borrower's personal income. Perfect for landlords expanding their portfolio or refinancing rental properties.",
              slug: 'dscr-bridge-plus',
              image: '/assets/customers/coastal-funding/programs/dscr-rental.jpg',
              highlights: [
                'Up to 80% LTV, no personal income verification',
                'Starting at 6.00%, DSCR ≥ 1.0x',
                'Up to 10 years interest-only, $100K-$3M loan size',
                'Varied prepayment options, ideal for portfolio expansion',
              ],
              rateRange: 'Starting at 6.00%',
              maxLtv: 80,
              termMonths: [12, 24, 60, 120],
              cta: { label: 'Pre-Qualify for DSCR', href: '/apply', loanType: 'dscr' },
            },
          ],
          showViewAll: false,
          padding: 'large',
        },

        // --- Our Lending Process ---
        {
          type: 'process-timeline',
          heading: 'Our Lending Process',
          subheading: 'From first conversation to funded deal — built for speed and clarity.',
          steps: [
            {
              number: 1,
              title: 'Submit Your Deal',
              description:
                'Tell us about the property, your experience, and your strategy. Quick online form or call us directly.',
              duration: '5 minutes',
            },
            {
              number: 2,
              title: 'Get a Term Sheet',
              description: 'A dedicated loan officer reviews your deal and delivers a clear, no-surprise term sheet.',
              duration: '24 hours',
            },
            {
              number: 3,
              title: 'Underwriting',
              description:
                'We complete valuation, title, and borrower verification — fast and without unnecessary back-and-forth.',
              duration: '3–5 days',
            },
            {
              number: 4,
              title: 'Close & Fund',
              description:
                'Sign documents and receive your wire. We coordinate with title and escrow for a seamless close.',
              duration: 'Same-day wire',
            },
          ],
          cta: { label: 'Start Your Application', href: '/apply', variant: 'primary' },
          padding: 'large',
          background: 'rgba(255,255,255,0.04)',
        },

        // --- CTA Banner ---
        {
          type: 'cta-banner',
          variant: 'full-width',
          headline: 'Not Sure Which Program Fits?',
          body: 'Tell us about your deal and we will match you with the right program. Pre-qualification takes 24 hours — no commitment.',
          ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
          ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // LEADERSHIP PAGE
    // =========================================================================
    team: {
      slug: 'leadership',
      title: 'Leadership',
      sections: [
        // --- Hero ---
        {
          type: 'hero',
          variant: 'primary',
          headline: 'Meet Our Experts',
          subheadline:
            'Experienced professionals dedicated to providing exceptional private lending solutions and building lasting partnerships with the real estate community.',
          backgroundImage: '/assets/customers/coastal-funding/leadership-hero.jpg',
          overlayOpacity: 0.65,
          padding: 'large',
        },

        // --- Team Grid (Full Bio) ---
        {
          type: 'team-grid',
          variant: 'full-bio',
          heading: 'Our Leadership Team',
          subheading:
            'Meet the experienced professionals who guide our mission to provide transparent and reliable private lending solutions.',
          members: [
            {
              name: 'David Diaz',
              title: 'Co-Founder & CEO',
              image: '/assets/customers/coastal-funding/team/david-diaz.jpg',
              bio: 'David brings more than two decades of experience in real estate finance, construction, and institutional investment. He has been directly involved in over 36,000 single-family and multifamily units, completed more than 1,200 renovations, built over 300 new homes, and transacted on more than 4,000 residential lots. His expertise spans capital markets, large-scale construction and renovation, and the integration of technology into real estate operations.\n\nDavid began his career in commercial mortgage banking, where he underwrote and marketed more than $2 billion in structured real estate debt for agency, life company, and conduit lenders. He transitioned into operational leadership with a focus on value-add investment and large-scale residential platforms. In senior roles at Waypoint Homes, FirstKey Homes, and 1Sharpe Capital, David oversaw construction, acquisition, and asset management strategies for institutional portfolios across multiple U.S. markets. His work helped shape the operational and financial frameworks now standard in the single-family rental sector.\n\nMost recently, he co-founded the Panther family of companies, focused on single-family rental development and management in Southwest Florida. As founder of Coastal Real Estate Funding, David applies his institutional experience to private lending — building a platform defined by speed, transparency, and disciplined execution. David is a graduate of Florida State University (MBA, Finance & Entrepreneurship) and the University of Central Florida (BS, Finance & Real Estate).',
              linkedIn: 'https://www.linkedin.com/company/coastal-real-estate-funding',
            },
            {
              name: 'Roger Lehman',
              title: 'Co-Founder & COO',
              image: '/assets/customers/coastal-funding/team/roger-lehman.jpg',
              bio: "Roger brings over a decade of experience across capital markets, real estate finance, and development. He began his career on Wall Street, working in equity and credit derivatives before transitioning into real estate investment and private credit. Roger has since held senior financial and capital markets roles within multifamily development, private lending, and institutional real estate platforms. At Australy International, Lendmarq, and Bluenest Development, he helped structure and finance residential and commercial projects while managing risk and investor relations across diverse capital stacks.\n\nMost recently, Roger serves as Director and Head of Capital Markets and Investor Relations at Sphere Properties, a vertically integrated real estate investment and development company based in Miami. His experience combines strategic financial leadership with operational execution in both debt and equity markets.\n\nRoger's career reflects a unique progression from institutional finance to hands-on real estate development and private lending. His experience in both capital markets and construction finance enables him to bridge investors and developers with practical, data-driven capital solutions. As Chief Operating Officer of Coastal Real Estate Funding, Roger leads operations, capital strategy, and platform scalability — ensuring that Coastal delivers disciplined, intelligent lending solutions nationwide. Roger holds a Bachelor's degree in Finance and International Business from the University of Connecticut and a Global Executive MBA from Duke University's Fuqua School of Business.",
              linkedIn: 'https://www.linkedin.com/company/coastal-real-estate-funding',
            },
          ],
          columns: 2,
          linkToProfiles: true,
          padding: 'large',
        },

        // --- Core Values (using FAQ to display values) ---
        {
          type: 'faq',
          variant: 'two-column',
          heading: 'Our Core Values',
          subheading: 'The principles that guide our leadership and drive our commitment to excellence',
          items: [
            {
              question: 'Transparency',
              answer:
                'Clear communication and honest dealings in every transaction. We believe trust is earned through openness and accountability at every stage of the lending process.',
              category: 'Values',
            },
            {
              question: 'Expertise',
              answer:
                'Deep knowledge of real estate markets and lending practices. Our leadership team brings decades of combined institutional and hands-on real estate experience.',
              category: 'Values',
            },
            {
              question: 'Reliability',
              answer:
                'Consistent delivery on our commitments and promises to borrowers. When we say we will close, we close — on time and on terms.',
              category: 'Values',
            },
            {
              question: 'Innovation',
              answer:
                'Continuously improving our processes and service offerings. We leverage technology and data to deliver faster, smarter lending solutions.',
              category: 'Values',
            },
          ],
          allowMultipleOpen: true,
          padding: 'large',
          background: 'rgba(10,37,64,0.25)',
        },

        // --- CTA Banner ---
        {
          type: 'cta-banner',
          variant: 'split-content',
          headline: 'Work Directly with Our Team',
          body: 'No account managers or call centers. When you work with Coastal, you work with the people on this page. Get pre-qualified in 24 hours.',
          ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
          ctaSecondary: {
            label: 'Call (239) 259-9829',
            href: 'tel:+12392599829',
            variant: 'outline',
          },
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // FUNDED DEALS PAGE
    // =========================================================================
    fundedDeals: {
      slug: 'funded-deals',
      title: 'Funded Deals',
      published: false,
      sections: [
        // --- Hero ---
        {
          type: 'hero',
          variant: 'minimal-text',
          headline: 'Deals We Have Funded Across Florida',
          subheadline:
            'Real projects, real results. See how we help Florida investors close deals fast with reliable capital.',
          padding: 'large',
        },

        // --- Funded Deals (Grid) ---
        {
          type: 'funded-deals',
          layout: 'grid',
          heading: 'Recent Projects',
          showTotalStats: true,
          linkToDetails: false,
          deals: [
            {
              id: WEBSITE_SEED_IDS.FUNDED_DEAL_1,
              propertyType: 'Single Family',
              location: 'Miami Beach, FL',
              loanAmount: 425000,
              loanProgram: 'Fix & Flip',
              image: '/assets/customers/coastal-funding/deals/deal-1.jpg',
              daysToClose: 5,
            },
            {
              id: WEBSITE_SEED_IDS.FUNDED_DEAL_2,
              propertyType: 'Single Family (Ground-Up)',
              location: 'Tampa, FL',
              loanAmount: 1200000,
              loanProgram: 'Ground-Up Construction',
              image: '/assets/customers/coastal-funding/deals/deal-2.jpg',
              daysToClose: 14,
            },
            {
              id: WEBSITE_SEED_IDS.FUNDED_DEAL_3,
              propertyType: 'Townhouse',
              location: 'Fort Lauderdale, FL',
              loanAmount: 680000,
              loanProgram: 'Bridge',
              image: '/assets/customers/coastal-funding/deals/deal-3.jpg',
              daysToClose: 7,
            },
            {
              id: WEBSITE_SEED_IDS.FUNDED_DEAL_4,
              propertyType: 'Duplex',
              location: 'Orlando, FL',
              loanAmount: 950000,
              loanProgram: 'DSCR Bridge Plus',
              image: '/assets/customers/coastal-funding/deals/deal-4.jpg',
              daysToClose: 10,
            },
            {
              id: WEBSITE_SEED_IDS.FUNDED_DEAL_5,
              propertyType: 'Mixed-Use (Retail + 4 Units)',
              location: 'Jacksonville, FL',
              loanAmount: 1800000,
              loanProgram: 'Bridge',
              image: '/assets/customers/coastal-funding/deals/deal-5.jpg',
              daysToClose: 9,
            },
          ],
          padding: 'large',
          background: 'rgba(10,37,64,0.25)',
        },

        // --- Stats Counter (Deal-focused) ---
        {
          type: 'stats-counter',
          stats: [
            { value: 500, suffix: '+', label: 'Deals Funded' },
            { value: 180, prefix: '$', suffix: 'M+', label: 'Total Originated' },
            { value: 7, suffix: '-Day', label: 'Average Close' },
            { value: 5, label: 'Florida Regions' },
          ],
          layout: 'row',
          animateOnScroll: true,
          animationDurationMs: 2500,
          padding: 'medium',
          background: 'rgba(255,255,255,0.04)',
        },

        // --- Testimonials (Grid - deal-related) ---
        {
          type: 'testimonials',
          variant: 'grid',
          heading: 'Borrower Experiences',
          testimonials: [
            {
              quote:
                'Coastal closed my first three deals in under 10 days each. Their team understood my renovation plan and got it done.',
              author: 'Marcus R.',
              authorTitle: 'Fix & Flip Investor',
              rating: 5,
              location: 'Miami, FL',
            },
            {
              quote:
                'When my conventional lender fell through two weeks before closing, Coastal stepped in and funded in 5 days.',
              author: 'David C.',
              authorTitle: 'Real Estate Investor',
              rating: 5,
              location: 'Fort Lauderdale, FL',
            },
          ],
          showRatings: true,
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // CONTACT PAGE
    // =========================================================================
    contact: {
      slug: 'contact',
      title: 'Contact Us',
      sections: [
        // --- Hero ---
        // padding: 'small' (not 'large') — minimal-text variant has no big
        // visual to balance large pads, and the lead-capture section right
        // below has its own default padding (~64px). Large hero pad here
        // creates a confusing 200px+ vertical void between the headline and
        // the form.
        {
          type: 'hero',
          variant: 'minimal-text',
          headline: "Let's Fund Your Next Deal",
          subheadline: 'Submit a loan inquiry below or call us directly. Pre-qualification typically takes 24 hours.',
          padding: 'small',
        },

        // --- Lead Capture ---
        {
          type: 'lead-capture',
          heading: 'Get Pre-Qualified',
          subheading:
            'Tell us about your project and one of our lending specialists will reach out within 1 business day.',
          layout: 'full-page',
          fields: [
            {
              name: 'name',
              label: 'Full Name',
              type: 'text',
              required: true,
              placeholder: 'John Smith',
            },
            {
              name: 'email',
              label: 'Email Address',
              type: 'email',
              required: true,
              placeholder: 'john@example.com',
            },
            {
              name: 'phone',
              label: 'Phone Number',
              type: 'phone',
              required: true,
              placeholder: '(555) 123-4567',
            },
            {
              name: 'propertyType',
              label: 'Property Type',
              type: 'select',
              required: true,
              options: [
                'Single Family',
                'Multi-Family (2-4 units)',
                'Commercial',
                'Mixed-Use',
                'Vacant Land / New Construction',
              ],
            },
            {
              name: 'loanType',
              label: 'Loan Program',
              type: 'select',
              required: true,
              options: ['Fix & Flip', 'Ground-Up Construction', 'Bridge Loan', 'DSCR Bridge Plus', 'Not Sure'],
            },
            {
              name: 'loanAmount',
              label: 'Estimated Loan Amount',
              type: 'select',
              options: ['Under $250K', '$250K - $500K', '$500K - $1M', '$1M - $3M', '$3M - $5M', '$5M+'],
            },
            {
              name: 'timeline',
              label: 'When Do You Need Funding?',
              type: 'select',
              options: ['Immediately (under contract)', 'Within 30 days', '1-3 months', 'Just exploring'],
            },
            {
              name: 'message',
              label: 'Tell Us About Your Project',
              type: 'textarea',
              placeholder: 'Property location, investment strategy, experience level...',
            },
          ],
          successMessage:
            'Thank you for reaching out! One of our lending specialists will be in touch within 1 business day.',
          padding: 'large',
        },

        // --- Contact Info ---
        {
          type: 'contact',
          heading: 'Other Ways to Reach Us',
          subheading: 'Prefer to talk? Call us directly or send an email.',
          layout: 'info-only',
          officeInfo: {
            phone: '(239) 259-9829',
            email: 'dave@coastal-funding.com',
            hours: 'Mon - Fri: 8am - 6pm ET',
            address: 'Naples, Florida',
          },
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // PRIVACY POLICY PAGE
    // =========================================================================
    privacyPolicy: {
      slug: 'privacy-policy',
      title: 'Privacy Policy',
      sections: [
        {
          type: 'hero',
          variant: 'minimal-text',
          headline: 'Privacy Policy',
          subheadline:
            'This Privacy Policy explains how Coastal Real Estate Funding LLC collects, uses, and protects your personal information.',
          padding: 'medium',
        },
        {
          type: 'faq',
          variant: 'accordion',
          heading: 'Privacy Policy Details',
          items: [
            {
              question: 'Information We Collect',
              answer:
                'We collect personal information you provide directly to us, including your name, email address, phone number, property details, and financial information when you submit a loan inquiry or application. We also collect usage data through cookies and similar technologies when you visit our website.',
              category: 'Collection',
            },
            {
              question: 'How We Use Your Information',
              answer:
                'We use your information to process loan inquiries and applications, communicate with you about our services, improve our website and services, comply with legal obligations, and send marketing communications (with your consent). We do not sell your personal information to third parties.',
              category: 'Usage',
            },
            {
              question: 'Information Sharing',
              answer:
                'We may share your information with service providers who assist in our operations, regulatory authorities as required by law, and business partners involved in processing your loan application. All third parties are contractually obligated to protect your information.',
              category: 'Sharing',
            },
            {
              question: 'Data Security',
              answer:
                'We implement industry-standard security measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of electronic transmission or storage is 100% secure.',
              category: 'Security',
            },
            {
              question: 'Your Rights',
              answer:
                'You have the right to access, correct, or delete your personal information. You may opt out of marketing communications at any time. To exercise these rights, contact us at dave@coastal-funding.com or call (239) 259-9829.',
              category: 'Rights',
            },
            {
              question: 'SMS / Text Message Communications',
              answer:
                'SMS consent is captured ONLY when you check the dedicated SMS opt-in box on a form on this site — submitting a form without checking that box does not enroll you in SMS. If you check the SMS box, you agree to receive transactional SMS messages from Coastal Real Estate Funding LLC about your inquiry, application, or loan — for example, status updates, document requests, scheduling, and reminders. Message frequency varies based on the status of your inquiry or loan. Msg & data rates may apply. You may opt out at any time by replying STOP to any message; reply HELP for assistance. Coastal Real Estate Funding will not share your mobile phone number, your SMS opt-in status, or any consent information with third parties or affiliates for their marketing or promotional purposes. SMS data is used solely to communicate with you about your inquiry, application, or loan. Coastal Real Estate Funding does NOT send promotional or marketing SMS through this opt-in.',
              category: 'SMS Communications',
            },
            {
              question: 'Phone Call Communications',
              answer:
                'Manually-dialed live-agent calls about an inquiry or loan you have started with us do not require prior express written consent under TCPA, and Coastal Real Estate Funding LLC may place those calls to the phone number you provide — for example, document or scheduling calls, status updates, and routine follow-up about an active loan — until that matter is resolved or you ask us to stop. Consent for autodialed and prerecorded/artificial-voice calls is SEPARATE and is captured ONLY when you check the dedicated voice opt-in box on a form on this site. If you check the voice box, you expressly consent to receive phone calls from Coastal Real Estate Funding LLC and our authorized agents about your inquiry, application, or loan, including calls placed using an automatic telephone dialing system (autodialer) or an artificial or prerecorded voice. Standard call rates from your carrier may apply. Consent to receive calls is not a condition of any purchase, loan, or service — you may still apply by contacting us directly. You may opt out of marketing or sales calls at any time by telling the caller "do not call," replying to any text message with STOP, or emailing dave@coastal-funding.com to be added to our internal Do Not Call list.',
              category: 'Phone Call Communications',
            },
            {
              question: 'Contact Us',
              answer:
                'If you have questions about this Privacy Policy, please contact Coastal Real Estate Funding LLC at dave@coastal-funding.com or (239) 259-9829.',
              category: 'Contact',
            },
          ],
          allowMultipleOpen: true,
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // TERMS OF SERVICE PAGE
    // =========================================================================
    termsOfService: {
      slug: 'terms-of-service',
      title: 'Terms of Service',
      sections: [
        {
          type: 'hero',
          variant: 'minimal-text',
          headline: 'Terms of Service',
          subheadline:
            'Please read these Terms of Service carefully before using the Coastal Real Estate Funding website.',
          padding: 'medium',
        },
        {
          type: 'faq',
          variant: 'accordion',
          heading: 'Terms of Service Details',
          items: [
            {
              question: 'Acceptance of Terms',
              answer:
                'By accessing or using the Coastal Real Estate Funding website, you agree to be bound by these Terms of Service. If you do not agree, please do not use our website or services.',
              category: 'General',
            },
            {
              question: 'Use of Website',
              answer:
                "This website is provided for informational purposes and to facilitate loan inquiries. You agree to use the website only for lawful purposes. You may not use the website in any way that could damage, disable, or impair the website or interfere with any other party's use.",
              category: 'Usage',
            },
            {
              question: 'No Commitment to Lend',
              answer:
                'Nothing on this website constitutes a commitment to lend or an offer of credit. All loan programs, rates, terms, and conditions are subject to change without notice. All loans are subject to credit approval and underwriting requirements.',
              category: 'Lending',
            },
            {
              question: 'Intellectual Property',
              answer:
                'All content on this website, including text, graphics, logos, and images, is the property of Coastal Real Estate Funding LLC and is protected by copyright and trademark laws. You may not reproduce, distribute, or create derivative works without our written permission.',
              category: 'IP',
            },
            {
              question: 'Limitation of Liability',
              answer:
                'Coastal Real Estate Funding LLC is not liable for any direct, indirect, incidental, or consequential damages arising from your use of this website. We make no warranties, express or implied, regarding the accuracy or completeness of information provided.',
              category: 'Liability',
            },
            {
              question: 'Governing Law',
              answer:
                'These Terms of Service are governed by the laws of the State of Florida. Any disputes shall be resolved in the courts of Lee County, Florida.',
              category: 'Legal',
            },

            // ----------------------------------------------------------------
            // SMS / A2P 10DLC TERMS (Twilio TCR registration — required items)
            //
            // The nine items below collectively satisfy Twilio's 2026-05-13
            // rejection feedback. Categories are tagged with
            // 'SMS Program' so they group together in any rendered FAQ.
            //
            //   ✓ Program/brand name             — "SMS Program Name"
            //   ✓ Program description            — "Program Description"
            //   ✓ Message and data rates         — "Message and Data Rates"
            //   ✓ Message frequency              — "Message Frequency"
            //   ✓ Customer support contact       — "Customer Support"
            //   ✓ Opt-out (STOP, bold)           — "Opt-Out: Reply **STOP**"
            //   ✓ Help (HELP, bold)              — "Help: Reply **HELP**"
            //   ✓ Link to Privacy Policy         — "Privacy Policy"
            //   ✓ Carrier liability disclaimer   — "Carrier Liability"
            //
            // HELP / STOP MUST render in bold per Twilio review. Inline
            // markdown bold (**STOP**, **HELP**) is parsed by
            // FAQAccordion's renderInlineBold helper (36-website-portal/
            // src/components/sections/FAQAccordion.tsx) into <strong>
            // spans at render time. No additional renderer changes needed.
            // ----------------------------------------------------------------
            {
              question: 'SMS Program Name',
              answer:
                'Coastal Real Estate Funding LLC SMS Program (operated by Coastal Real Estate Funding LLC, doing business as Coastal Funding).',
              category: 'SMS Program',
            },
            {
              question: 'Program Description',
              answer:
                'The Coastal Real Estate Funding SMS Program sends transactional, customer-care messages to consumers and partners who have started an inquiry, application, or loan with us. Messages include application status updates, document requests, scheduling and reminder notifications, and replies to questions you send us. This program is strictly transactional — Coastal Real Estate Funding does NOT send promotional or marketing SMS through this program.',
              category: 'SMS Program',
            },
            {
              question: 'Message and Data Rates',
              answer:
                'Message and data rates may apply. Coastal Real Estate Funding LLC does not charge for sending or receiving SMS messages, but your wireless carrier may bill you for messages and data usage according to your plan.',
              category: 'SMS Program',
            },
            {
              question: 'Message Frequency',
              answer:
                'Message frequency varies based on the status and activity of your inquiry, application, or loan. You will only receive messages that relate to a specific request, application, or loan you have started with Coastal Real Estate Funding LLC.',
              category: 'SMS Program',
            },
            {
              question: 'Customer Support',
              answer:
                'For questions or help with the SMS Program, contact Coastal Real Estate Funding LLC at dave@coastal-funding.com or call (239) 259-9829 during normal business hours (Monday–Friday, 9:00 AM – 5:00 PM Eastern). You may also reply HELP to any message to receive support contact information by SMS.',
              category: 'SMS Program',
            },
            {
              question: 'Opt-Out: Reply **STOP**',
              answer:
                'You can opt out of the SMS Program at any time by replying **STOP** to any message you receive. After you send **STOP**, you will receive a single confirmation message and no further SMS messages will be sent. To rejoin the program after opting out, submit a new inquiry on coastal-funding.com or contact us directly.',
              category: 'SMS Program',
            },
            {
              question: 'Help: Reply **HELP**',
              answer:
                'For help at any time, reply **HELP** to any message you receive from Coastal Real Estate Funding LLC. You will receive a reply containing the program name and the customer support contact information (dave@coastal-funding.com / (239) 259-9829).',
              category: 'SMS Program',
            },
            {
              question: 'Privacy Policy',
              answer:
                'Your participation in the SMS Program is governed by the Coastal Real Estate Funding Privacy Policy, available at https://coastal-funding.com/privacy-policy. Coastal Real Estate Funding will not share your mobile phone number, your SMS opt-in status, or any consent information with third parties or affiliates for their marketing or promotional purposes.',
              category: 'SMS Program',
            },
            {
              question: 'Carrier Liability',
              answer:
                'Carriers are not liable for any delayed or undelivered messages. Message delivery is dependent on wireless carrier infrastructure and is provided on a best-effort basis.',
              category: 'SMS Program',
            },
          ],
          allowMultipleOpen: true,
          padding: 'large',
        },
      ],
    },

    // =========================================================================
    // PARTNERS LANDING PAGE
    // =========================================================================
    // Synced from production 2026-04-29 — drift was found while preparing the
    // voice-consent migration. Production carries TWO keys (`partner` and
    // `partners`) that both render at /partners; the duplicate is a legacy
    // bug worth cleaning up separately, but reflected here so future
    // customer deployments aren't stripped of this page.
    partner: {
      slug: 'partners',
      title: 'Partners',
      seo: {
        title: 'Partner With Coastal Funding',
        description: 'Join our network of lending partners. Apply to become a Coastal Funding partner.',
      },
      sections: [
        {
          type: 'hero',
          variant: 'split-screen',
          padding: 'large',
          headline: 'Grow Your Business With Coastal Funding',
          subheadline:
            'Join our network of brokers, lenders, and real estate professionals. Fast closings, competitive rates, and a dedicated partner team behind every deal.',
          backgroundImage: '/assets/customers/coastal-funding/hero-bg.jpg',
          ctaPrimary: { label: 'Apply Below', href: '#partner-application', variant: 'primary' },
          ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
        },
        {
          id: 'partner-application',
          type: 'partner-application',
          heading: 'Apply to Become a Partner',
          subheading: 'Complete the form below and our team will review your application within 48 hours.',
          leadSource: 'website_partner_application',
          successMessage:
            'Thank you for your application! Our partnerships team will review it and reach out within 2 business days.',
        },
      ],
      published: true,
    },
    // Legacy duplicate (matches production state, see comment above).
    // Marked unpublished so it's a faithful mirror of the legacy row but
    // doesn't collide with `partner` on the `/partners` route or surface
    // in SEO. Has a slug/title for type-completeness; not actually served.
    partners: {
      slug: 'partners',
      title: 'Partners',
      published: false,
      sections: [
        {
          type: 'hero',
          variant: 'split-screen',
          padding: 'large',
          headline: 'Partner With Coastal Funding',
          subheadline:
            'Join our network of brokers, lenders, and real estate professionals. Fast closings, competitive rates, and a dedicated partner team behind every deal.',
          backgroundImage: '/assets/customers/coastal-funding/hero-bg.jpg',
          ctaPrimary: { label: 'Apply Below', href: '#partner-application', variant: 'primary' },
          ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
        },
        {
          id: 'partner-application',
          type: 'partner-application',
          heading: 'Apply to Become a Partner',
          subheading: 'Complete the form below and our team will review your application within 48 hours.',
          leadSource: 'website_partner_application',
          successMessage:
            'Thank you for your application! Our partnerships team will review it and reach out within 2 business days.',
        },
      ],
    },

    // =========================================================================
    // CUSTOM PAGES
    // =========================================================================
    custom: [
      // =====================================================================
      // GEOGRAPHIC FOCUS PAGE
      // =====================================================================
      {
        slug: 'geographic',
        title: 'Geographic Focus',
        sections: [
          // --- Hero ---
          {
            type: 'hero',
            variant: 'primary',
            headline: 'Market Coverage',
            subheadline:
              'Strategic coverage across the most dynamic real estate markets, providing local expertise and regional insights for your investment success.',
            backgroundImage: '/assets/customers/coastal-funding/hero-bg.jpg',
            overlayOpacity: 0.6,
            padding: 'large',
          },

          // --- Service Areas (using loan-programs to display regions as cards) ---
          {
            type: 'loan-programs',
            heading: 'Our Service Areas',
            subheading:
              'We provide private lending services across key markets, leveraging deep local knowledge and market expertise.',
            layout: 'grid',
            columns: 3,
            programs: [
              {
                id: 'south-florida',
                name: 'South Florida',
                shortDescription:
                  'A dynamic and high-growth region known for its luxury real estate, vibrant tourism, and strong international investment. Ideal for high-value fix & flip and new construction projects.',
                slug: 'geographic#south-florida',
                image: '/assets/customers/coastal-funding/geographic/south-florida.jpg',
                highlights: [
                  'Key Markets: Miami, Fort Lauderdale, West Palm Beach',
                  'Counties: Miami-Dade, Broward, Palm Beach',
                ],
                cta: { label: 'Get Pre-Qualified in South FL', href: '/apply?region=south-florida' },
              },
              {
                id: 'central-florida',
                name: 'Central Florida',
                shortDescription:
                  'Home to major theme parks and a rapidly expanding population, Central Florida offers robust opportunities in residential development, vacation rentals, and commercial properties.',
                slug: 'geographic#central-florida',
                image: '/assets/customers/coastal-funding/geographic/central-florida.jpg',
                highlights: ['Key Markets: Orlando, Kissimmee, Lakeland', 'Counties: Orange, Osceola, Polk, Seminole'],
                cta: { label: 'Get Pre-Qualified in Central FL', href: '/apply?region=central-florida' },
              },
              {
                id: 'tampa-bay',
                name: 'Tampa Bay',
                shortDescription:
                  'A diverse economic hub with a strong job market, beautiful coastlines, and a growing tech sector. Excellent for both residential and commercial bridge loans and new developments.',
                slug: 'geographic#tampa-bay',
                image: '/assets/customers/coastal-funding/geographic/tampa-bay.jpg',
                highlights: [
                  'Key Markets: Tampa, St. Petersburg, Clearwater',
                  'Counties: Hillsborough, Pinellas, Pasco',
                ],
                cta: { label: 'Get Pre-Qualified in Tampa Bay', href: '/apply?region=tampa-bay' },
              },
              {
                id: 'northeast-florida',
                name: 'Northeast Florida',
                shortDescription:
                  'Characterized by its expanding urban centers, historic charm, and strategic port access. This region presents opportunities for both residential and commercial property investments.',
                slug: 'geographic#northeast-florida',
                image: '/assets/customers/coastal-funding/geographic/northeast-florida.jpg',
                highlights: [
                  'Key Markets: Jacksonville, St. Augustine, Gainesville',
                  'Counties: Duval, St. Johns, Clay, Alachua',
                ],
                cta: { label: 'Get Pre-Qualified in NE FL', href: '/apply?region=northeast-florida' },
              },
              {
                id: 'southwest-florida',
                name: 'Southwest Florida',
                shortDescription:
                  'A popular destination for retirees and seasonal residents, offering a strong market for luxury homes, vacation properties, and redevelopment projects along the Gulf Coast.',
                slug: 'geographic#southwest-florida',
                image: '/assets/customers/coastal-funding/geographic/southwest-florida.jpg',
                highlights: [
                  'Key Markets: Naples, Fort Myers, Sarasota',
                  'Counties: Collier, Lee, Sarasota, Charlotte',
                ],
                cta: { label: 'Get Pre-Qualified in SW FL', href: '/apply?region=southwest-florida' },
              },
            ],
            showViewAll: false,
            padding: 'large',
          },

          // --- Local Market Expertise (FAQ) ---
          {
            type: 'faq',
            variant: 'two-column',
            heading: 'Local Market Expertise',
            subheading:
              'Our deep understanding of diverse real estate markets enables us to provide informed lending decisions and valuable insights to our borrowers.',
            items: [
              {
                question: 'Market Analysis',
                answer:
                  'Comprehensive evaluation of local market conditions, trends, and opportunities. We track inventory levels, pricing trends, and economic indicators across all our service areas to provide data-driven lending decisions.',
                category: 'Expertise',
              },
              {
                question: 'Property Valuation',
                answer:
                  'Accurate assessment based on local comparables and market dynamics. Our team maintains deep knowledge of neighborhood-level pricing and development trends across Florida.',
                category: 'Expertise',
              },
              {
                question: 'Risk Assessment',
                answer:
                  'Location-specific risk evaluation considering local factors and regulations. We account for hurricane codes, flood zones, insurance markets, and municipal permitting timelines.',
                category: 'Expertise',
              },
              {
                question: 'Growth Potential',
                answer:
                  'Identification of emerging markets and investment opportunities. We monitor population growth, infrastructure development, and economic expansion to help borrowers find the best opportunities.',
                category: 'Expertise',
              },
            ],
            allowMultipleOpen: true,
            padding: 'large',
            background: 'rgba(255,255,255,0.04)',
          },

          // --- Coverage Benefits (Stats) ---
          {
            type: 'stats-counter',
            heading: 'Coverage Benefits',
            stats: [
              { value: 5, label: 'Florida Regions Covered' },
              { value: 20, suffix: '+', label: 'Counties Served' },
              { value: 15, suffix: '+', label: 'Key Markets' },
              { value: 20, suffix: '+', label: 'Years Local Experience' },
            ],
            layout: 'row',
            animateOnScroll: true,
            animationDurationMs: 2500,
            padding: 'medium',
            background: 'rgba(10,37,64,0.25)',
          },

          // --- Strategic Expansion CTA ---
          {
            type: 'cta-banner',
            variant: 'split-content',
            headline: 'Investing in Florida?',
            body: 'Our team lives, builds, and lends across these markets. Whether you are flipping in Miami or building in Tampa, we know the neighborhoods, the contractors, and the timelines.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
            ctaSecondary: {
              label: 'Call (239) 259-9829',
              href: 'tel:+12392599829',
              variant: 'outline',
            },
            padding: 'large',
          },
        ],
      },

      // =====================================================================
      // LOAN PROGRAM DETAIL PAGES (linked from homepage "Learn More")
      // =====================================================================
      {
        slug: 'services/fix-and-flip',
        title: 'Fix & Flip Loans',
        seo: {
          title: 'Fix & Flip Loans',
          description:
            'Fast funding for property renovation and resale. Up to 100% of purchase price and rehab budget. Close in 5-10 days.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'split-screen',
            headline: 'Fix & Flip Loans',
            subheadline:
              'Short-term, asset-based capital for acquiring and renovating residential properties for resale. Designed for active investors who value speed, leverage, and predictable execution.',
            backgroundImage: '/assets/customers/coastal-funding/programs/fix-and-flip.jpg',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=fix-flip', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'loan-program-detail',
            programSlug: 'fix-and-flip',
            showRateTable: true,
            showQualifications: true,
            showCta: true,
            cta: { label: 'Pre-Qualify for Fix & Flip', href: '/apply?loanType=fix-flip', variant: 'primary' },
            relatedProgramSlugs: ['construction', 'bridge'],
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Ready to Fund Your Next Flip?',
            body: 'Pre-qualification in 24 hours. No commitment, no hard credit pull.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=fix-flip', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'medium',
          },
        ],
      },
      {
        slug: 'services/construction',
        title: 'Ground-Up Construction Loans',
        seo: {
          title: 'Ground-Up Construction Loans',
          description:
            'Staged construction financing for spec builds and development. Up to 90% loan-to-total-cost. Milestone-based draw schedules.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'split-screen',
            headline: 'Ground-Up Construction',
            subheadline:
              'Staged construction financing for spec builds and investor-driven development projects. Structured around real-world builder timelines and milestone-based draw schedules.',
            backgroundImage: '/assets/customers/coastal-funding/programs/construction.jpg',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=construction', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'loan-program-detail',
            programSlug: 'construction',
            showRateTable: true,
            showQualifications: true,
            showCta: true,
            cta: { label: 'Pre-Qualify for Construction', href: '/apply?loanType=construction', variant: 'primary' },
            relatedProgramSlugs: ['fix-and-flip', 'bridge'],
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Ready to Build?',
            body: '72-hour draw funding after inspection. Pre-qualification in 24 hours.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=construction', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'medium',
          },
        ],
      },
      {
        slug: 'services/bridge',
        title: 'Bridge Loans',
        seo: {
          title: 'Bridge Loans',
          description:
            'Flexible short-term financing for acquisitions, refinances, and transitional properties. Up to 80% LTV. Close in 5-10 days.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'split-screen',
            headline: 'Bridge Loans',
            subheadline:
              'Flexible short-term capital for acquisitions, refinances, cash-outs, and transitional properties. Ideal for investors executing time-sensitive opportunities.',
            backgroundImage: '/assets/customers/coastal-funding/programs/bridge.jpg',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=bridge', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'loan-program-detail',
            programSlug: 'bridge',
            showRateTable: true,
            showQualifications: true,
            showCta: true,
            cta: { label: 'Pre-Qualify for Bridge', href: '/apply?loanType=bridge', variant: 'primary' },
            relatedProgramSlugs: ['fix-and-flip', 'dscr-bridge-plus'],
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Time-Sensitive Deal?',
            body: 'We have closed in as few as 3 business days. Pre-qualification in 24 hours.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=bridge', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'medium',
          },
        ],
      },
      {
        slug: 'services/dscr-bridge-plus',
        title: 'DSCR Bridge Plus',
        seo: {
          title: 'DSCR / Rental Loans',
          description:
            'Cash-flow-based financing for rental investors. No personal income verification. Starting at 6.00%. Up to 80% LTV.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'split-screen',
            headline: 'DSCR / Rental Loans',
            subheadline:
              'Cash-flow-based financing for rental investors. Qualify based on property income — no personal income verification required.',
            backgroundImage: '/assets/customers/coastal-funding/programs/dscr-rental.jpg',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=dscr', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'loan-program-detail',
            programSlug: 'dscr-bridge-plus',
            showRateTable: true,
            showQualifications: true,
            showCta: true,
            cta: { label: 'Pre-Qualify for DSCR', href: '/apply?loanType=dscr', variant: 'primary' },
            relatedProgramSlugs: ['bridge', 'fix-and-flip'],
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Qualify Based on Cash Flow',
            body: 'No income docs required. Get pre-qualified based on your property rental income.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply?loanType=dscr', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'medium',
          },
        ],
      },

      // =====================================================================
      // FIX & FLIP CALCULATOR PAGE
      // =====================================================================
      {
        slug: 'calculators/fix-and-flip',
        title: 'Fix & Flip Profit Calculator',
        seo: {
          title: 'Fix & Flip Profit Calculator',
          description:
            'Calculate your fix & flip profit, ROI, cash to close, and holding costs. Free real estate investment calculator for Florida investors.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'minimal-text',
            headline: 'Fix & Flip Profit Calculator',
            subheadline:
              'Estimate your net profit, ROI, and cash needed before you make an offer. Adjust deal inputs in real time to find the right numbers.',
            padding: 'medium',
          },
          {
            type: 'fix-flip-calculator',
            heading: 'Run Your Numbers',
            subheading: 'Drag the sliders to match your deal. Results update instantly.',
            defaults: {
              purchasePrice: 300000,
              arv: 450000,
              rehabBudget: 75000,
              ltcPercent: 90,
              rate: 9.5,
              points: 2,
              holdingMonths: 8,
              monthlyHoldingCosts: 1500,
              sellingCommission: 5,
            },
            cta: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'faq',
            variant: 'accordion',
            heading: 'Fix & Flip FAQ',
            items: [
              {
                question: 'What is Loan-to-Cost (LTC)?',
                answer:
                  'LTC is the ratio of the loan amount to the total project cost (purchase price + rehab). For example, 90% LTC on a $375K total cost means a $337,500 loan.',
                category: 'Basics',
              },
              {
                question: 'How are origination points calculated?',
                answer:
                  'Points are a percentage of the loan amount charged at closing. 2 points on a $300K loan = $6,000 origination fee.',
                category: 'Costs',
              },
              {
                question: 'What are typical holding costs?',
                answer:
                  'Holding costs include property taxes, insurance, utilities, HOA, and maintenance. In Florida, budget $1,000-$2,500/month depending on property size and location.',
                category: 'Costs',
              },
              {
                question: 'How do I estimate ARV accurately?',
                answer:
                  'Use comparable sales from the last 90 days within 0.5 miles. Focus on similar square footage, bed/bath count, and condition. Get a professional BPO for deals over $500K.',
                category: 'Strategy',
              },
            ],
            allowMultipleOpen: true,
            padding: 'large',
            background: 'rgba(255,255,255,0.04)',
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Ready to Fund Your Flip?',
            body: 'Get pre-qualified in 24 hours. Our team will review your deal and provide a term sheet.',
            ctaPrimary: {
              label: 'Pre-Qualify for Fix & Flip',
              href: '/apply',
              loanType: 'fix-flip',
              variant: 'primary',
            },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'large',
          },
        ],
      },

      // =====================================================================
      // DSCR CALCULATOR PAGE
      // =====================================================================
      {
        slug: 'calculators/dscr',
        title: 'DSCR Calculator',
        seo: {
          title: 'DSCR Calculator',
          description:
            'Calculate your Debt Service Coverage Ratio (DSCR) for rental property investments. Free DSCR calculator for Florida real estate investors.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'minimal-text',
            headline: 'DSCR Calculator',
            subheadline:
              'Calculate your Debt Service Coverage Ratio to see if your rental property qualifies. DSCR loans require no income verification.',
            padding: 'medium',
          },
          {
            type: 'dscr-calculator',
            heading: 'Calculate Your DSCR',
            subheading:
              'Enter your property details to see if the rental income covers the debt service. A DSCR of 1.25x or higher is ideal.',
            defaults: {
              propertyValue: 350000,
              monthlyRent: 2800,
              vacancyPercent: 5,
              rate: 7.5,
              termYears: 30,
              ltvPercent: 75,
              monthlyTaxes: 250,
              monthlyInsurance: 150,
              monthlyHOA: 0,
            },
            minDSCR: 1.0,
            cta: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'faq',
            variant: 'accordion',
            heading: 'DSCR Loan FAQ',
            items: [
              {
                question: 'What is DSCR?',
                answer:
                  'Debt Service Coverage Ratio measures whether a property generates enough income to cover its debt payments. DSCR = Net Operating Income / Total Debt Service. A ratio above 1.0 means the property cash flows.',
                category: 'Basics',
              },
              {
                question: 'What DSCR do I need to qualify?',
                answer:
                  'Most DSCR lenders require a minimum of 1.0x, but 1.25x or higher gets the best rates. Some programs accept 0.75x DSCR with compensating factors.',
                category: 'Qualification',
              },
              {
                question: 'Do I need to verify my personal income?',
                answer:
                  "No. DSCR loans qualify based on the property's rental income, not your personal income. This makes them ideal for self-employed investors and portfolio holders.",
                category: 'Qualification',
              },
              {
                question: 'What counts as Net Operating Income (NOI)?',
                answer:
                  'NOI = Gross Rental Income - Vacancy - Taxes - Insurance - HOA - Management. It does NOT include mortgage payments.',
                category: 'Basics',
              },
            ],
            allowMultipleOpen: true,
            padding: 'large',
            background: 'rgba(255,255,255,0.04)',
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Qualify Based on Cash Flow, Not Income',
            body: "Our DSCR Bridge Plus program requires no income documentation. Get pre-qualified based on your property's rental income.",
            ctaPrimary: { label: 'Pre-Qualify for DSCR', href: '/apply', loanType: 'dscr', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'large',
          },
        ],
      },

      // =====================================================================
      // APPLY / PRE-QUALIFICATION PAGE
      // =====================================================================
      {
        slug: 'apply',
        title: 'Apply Now',
        seo: {
          title: 'Apply for a Loan',
          description:
            'Get pre-qualified for a private real estate loan in 24 hours. Fix & flip, construction, bridge, and DSCR loans for Florida investors.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'minimal-text',
            headline: 'Get Pre-Qualified in 24 Hours',
            subheadline:
              'Answer a few questions about your project and experience. A lending specialist will follow up within 1 business day.',
            padding: 'medium',
          },
          {
            type: 'pre-qual-wizard',
            heading: 'Pre-Qualification Application',
            subheading: 'Takes about 2 minutes to complete',
            loanTypes: [
              {
                id: 'fix-flip',
                label: 'Fix & Flip',
                description: 'Purchase, renovate, and resell residential properties',
              },
              {
                id: 'construction',
                label: 'Ground-Up Construction',
                description: 'Build new residential or commercial properties from scratch',
              },
              {
                id: 'bridge',
                label: 'Bridge Loan',
                description: 'Short-term financing for acquisitions, refinances, or time-sensitive deals',
              },
              {
                id: 'dscr',
                label: 'DSCR / Rental',
                description: 'Investment property loans based on rental income — no income docs required',
              },
            ],
            successMessage:
              'Your application has been received! A lending specialist will contact you within 1 business day.',
            leadSource: 'website-pre-qual',
            padding: 'large',
          },
        ],
      },

      // =====================================================================
      // HOW IT WORKS / PROCESS PAGE
      // =====================================================================
      {
        slug: 'process',
        title: 'How It Works',
        seo: {
          title: 'How It Works',
          description:
            'Learn how to get funded in as few as 5 days. Our streamlined process takes you from application to closing fast.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'primary',
            headline: 'How It Works',
            subheadline:
              'From first call to funding, our streamlined process is designed to get you to the closing table fast.',
            backgroundImage: '/assets/customers/coastal-funding/hero-bg.jpg',
            overlayOpacity: 0.65,
            padding: 'large',
          },
          {
            type: 'process-timeline',
            heading: 'Your Path to Funding',
            subheading: 'Five simple steps from initial inquiry to funded deal.',
            steps: [
              {
                number: 1,
                title: 'Submit Inquiry',
                description: 'Tell us about your deal using our quick online form or call us directly.',
                icon: 'Description',
                duration: '5 minutes',
              },
              {
                number: 2,
                title: 'Speak with a Specialist',
                description: 'A dedicated loan officer reviews your scenario and structures the best terms.',
                icon: 'Phone',
                duration: 'Same day',
              },
              {
                number: 3,
                title: 'Term Sheet & Approval',
                description: 'Receive a detailed term sheet. Once accepted, we begin underwriting immediately.',
                icon: 'Assignment',
                duration: '24 hours',
              },
              {
                number: 4,
                title: 'Underwriting & Diligence',
                description: 'Our team completes property valuation, title review, and borrower verification.',
                icon: 'FactCheck',
                duration: '2-5 days',
              },
              {
                number: 5,
                title: 'Closing & Funding',
                description:
                  'Sign documents and receive funds. We coordinate with title and escrow for a smooth close.',
                icon: 'AccountBalance',
                duration: 'Same day wire',
              },
            ],
            cta: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
            padding: 'large',
          },
          {
            type: 'stats-counter',
            stats: [
              { value: 5, suffix: '-Day', label: 'Fastest Close' },
              { value: 24, suffix: 'hr', label: 'Term Sheet Turnaround' },
              { value: 48, suffix: 'hr', label: 'Construction Draws' },
              { value: 98, suffix: '%', label: 'Funded to Approval Ratio' },
            ],
            layout: 'row',
            animateOnScroll: true,
            animationDurationMs: 2500,
            padding: 'medium',
            background: 'rgba(255,255,255,0.04)',
          },
          {
            type: 'testimonials',
            variant: 'carousel',
            heading: 'Borrowers Who Closed Fast',
            testimonials: [
              {
                quote:
                  'Coastal closed my first three deals in under 10 days each. Their team understood my renovation plan and got it done.',
                author: 'Marcus R.',
                authorTitle: 'Fix & Flip Investor',
                rating: 5,
                location: 'Miami, FL',
              },
              {
                quote:
                  'When my conventional lender fell through two weeks before closing, Coastal stepped in and funded in 5 days.',
                author: 'David C.',
                authorTitle: 'Real Estate Investor',
                rating: 5,
                location: 'Fort Lauderdale, FL',
              },
            ],
            showRatings: true,
            padding: 'large',
          },
          {
            type: 'cta-banner',
            variant: 'full-width',
            headline: 'Ready to Move on Your Next Deal?',
            body: 'Get pre-qualified in 24 hours. No commitment, no hard credit pull.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'large',
          },
        ],
      },

      // =====================================================================
      // GLOSSARY PAGE
      // =====================================================================
      {
        slug: 'resources/glossary',
        title: 'Lending Glossary',
        seo: {
          title: 'Private Lending Glossary',
          description:
            'Understand private lending terms. Our glossary covers ARV, DSCR, LTV, hard money, bridge loans, and more for real estate investors.',
        },
        sections: [
          {
            type: 'hero',
            variant: 'minimal-text',
            headline: 'Lending Glossary',
            subheadline:
              'Not sure what a term means? Browse our comprehensive glossary of private lending and real estate investment terms.',
            padding: 'medium',
          },
          {
            type: 'glossary',
            heading: 'Terms A-Z',
            showLetterNav: true,
            showSearch: true,
            terms: [
              {
                term: 'After Repair Value (ARV)',
                definition:
                  'The estimated value of a property after all renovations are complete. Used to determine the profitability of fix & flip projects and maximum loan amounts.',
                category: 'Valuation',
              },
              {
                term: 'Amortization',
                definition:
                  'The process of paying off a loan through regular scheduled payments of principal and interest over a set period.',
                category: 'Loan Structure',
              },
              {
                term: 'Bridge Loan',
                definition:
                  'Short-term financing used to bridge the gap between purchasing a property and obtaining permanent financing or selling the property.',
                category: 'Loan Types',
              },
              {
                term: 'Cash to Close',
                definition:
                  'The total amount of cash a borrower needs at closing, including down payment, origination fees, and third-party closing costs.',
                category: 'Costs',
              },
              {
                term: 'Construction Draw',
                definition:
                  'A disbursement of loan funds during the construction process, typically released after inspection of completed work at each project milestone.',
                category: 'Construction',
              },
              {
                term: 'Debt Service Coverage Ratio (DSCR)',
                definition:
                  "The ratio of a property's net operating income to its total debt service payments. A DSCR above 1.0 means the property generates enough income to cover its debt.",
                category: 'Metrics',
              },
              {
                term: 'Down Payment',
                definition:
                  'The portion of the purchase price paid upfront by the borrower at closing. Private lenders typically require 10-25% down.',
                category: 'Costs',
              },
              {
                term: 'Draw Schedule',
                definition:
                  'A predetermined plan outlining when and how much money will be released during a construction project, tied to completion milestones.',
                category: 'Construction',
              },
              {
                term: 'Exit Strategy',
                definition:
                  "The borrower's plan to repay the loan, such as selling the property (fix & flip) or refinancing into a long-term mortgage (DSCR rental).",
                category: 'Strategy',
              },
              {
                term: 'Fix & Flip',
                definition:
                  'An investment strategy where an investor purchases a property, renovates it, and sells it for profit within a short time frame (typically 6-18 months).',
                category: 'Strategy',
              },
              {
                term: 'Ground-Up Construction',
                definition:
                  'Building a new structure from scratch on vacant land or after demolishing an existing structure. Requires specialized construction financing.',
                category: 'Construction',
              },
              {
                term: 'Hard Money Loan',
                definition:
                  "A short-term, asset-based loan secured by real estate. Funded by private lenders rather than banks, with approval based primarily on the property's value.",
                category: 'Loan Types',
              },
              {
                term: 'Holding Costs',
                definition:
                  'Monthly expenses incurred while owning a property, including loan interest, property taxes, insurance, utilities, and maintenance.',
                category: 'Costs',
              },
              {
                term: 'Interest-Only Payment',
                definition:
                  'A payment structure where the borrower pays only the interest on the loan each month, with the principal due at maturity. Common in short-term private loans.',
                category: 'Loan Structure',
              },
              {
                term: 'Loan-to-Cost (LTC)',
                definition:
                  'The ratio of the loan amount to the total project cost (purchase price + renovation budget). A 90% LTC means the lender finances 90% of the total cost.',
                category: 'Metrics',
              },
              {
                term: 'Loan-to-Value (LTV)',
                definition:
                  "The ratio of the loan amount to the property's current market value or purchase price, whichever is lower. Used to assess lending risk.",
                category: 'Metrics',
              },
              {
                term: 'Net Operating Income (NOI)',
                definition:
                  "A property's total income minus operating expenses (taxes, insurance, management, maintenance), excluding mortgage payments. Key metric for DSCR calculations.",
                category: 'Metrics',
              },
              {
                term: 'Origination Fee',
                definition:
                  'A fee charged by the lender at closing, expressed as a percentage (points) of the loan amount. Covers the cost of processing and underwriting the loan.',
                category: 'Costs',
              },
              {
                term: 'Points',
                definition:
                  'A fee equal to 1% of the loan amount. For example, 2 points on a $300,000 loan equals $6,000. Charged as an origination fee at closing.',
                category: 'Costs',
              },
              {
                term: 'Pre-Qualification',
                definition:
                  "An initial assessment of a borrower's eligibility for a loan, based on self-reported information. Not a commitment to lend but indicates likely approval.",
                category: 'Process',
              },
              {
                term: 'Private Lender',
                definition:
                  'A non-bank entity that provides real estate financing, typically with faster approval, more flexible terms, and higher rates than conventional lenders.',
                category: 'Loan Types',
              },
              {
                term: 'Rehab Budget',
                definition:
                  'The estimated total cost of renovating a property, including materials, labor, permits, and a contingency reserve (typically 15-20%).',
                category: 'Construction',
              },
              {
                term: 'Return on Investment (ROI)',
                definition:
                  'The percentage return on capital invested. For fix & flip, ROI = Net Profit / Total Cash Invested x 100.',
                category: 'Metrics',
              },
              {
                term: 'Scope of Work',
                definition:
                  'A detailed document listing all renovation tasks, materials, and costs for a fix & flip or construction project. Required for loan approval.',
                category: 'Construction',
              },
              {
                term: 'Term Sheet',
                definition:
                  'A document outlining the proposed loan terms including rate, LTV, fees, and conditions. Issued after initial review, before full underwriting.',
                category: 'Process',
              },
              {
                term: 'Title Insurance',
                definition:
                  'Insurance that protects the lender and borrower against claims of ownership or liens on the property. Required at closing.',
                category: 'Costs',
              },
              {
                term: 'Underwriting',
                definition:
                  'The process of evaluating a loan application by analyzing the property value, borrower experience, project feasibility, and exit strategy.',
                category: 'Process',
              },
              {
                term: 'Vacancy Rate',
                definition:
                  'The percentage of time a rental property is expected to be unoccupied. Typically 5-10% for residential rentals, used in DSCR calculations.',
                category: 'Metrics',
              },
            ],
            padding: 'large',
          },
          {
            type: 'cta-banner',
            variant: 'inset-card',
            headline: 'Still Have Questions?',
            body: 'Our lending specialists are happy to walk you through any terms or explain how our loan programs work.',
            ctaPrimary: { label: 'Get Pre-Qualified', href: '/apply', variant: 'primary' },
            ctaSecondary: { label: 'Call (239) 259-9829', href: 'tel:+12392599829', variant: 'outline' },
            padding: 'large',
          },
        ],
      },
    ],
  },
};

// =============================================================================
// FUNDED DEALS (seeded in CRM Service)
// =============================================================================

export interface FundedDealSeed {
  id: string;
  businessId: string;
  title: string;
  propertyPhotos: string[];
  propertyType: string;
  city: string;
  state: string;
  loanType: string;
  loanAmount: number;
  ltv: number | null;
  fundingTimelineDays: number | null;
  dealHighlights: string[];
  borrowerTestimonial: string | null;
  closingDate: Date;
  status: string;
  featured: boolean;
  sortOrder: number;
}

export const COASTAL_FUNDING_FUNDED_DEALS: FundedDealSeed[] = [
  {
    id: WEBSITE_SEED_IDS.FUNDED_DEAL_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    title: 'Miami Beach Fix & Flip',
    propertyPhotos: ['/assets/customers/coastal-funding/deals/deal-1.jpg'],
    propertyType: 'Single Family',
    city: 'Miami Beach',
    state: 'FL',
    loanType: 'Fix & Flip',
    loanAmount: 425000,
    ltv: 85,
    fundingTimelineDays: 5,
    dealHighlights: ['Full renovation scope', 'ARV $680K', 'Closed in 5 days', 'Repeat borrower'],
    borrowerTestimonial:
      'Coastal closed this deal in 5 days flat. Their team understood the renovation plan and moved fast.',
    closingDate: new Date('2025-11-15'),
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 1,
  },
  {
    id: WEBSITE_SEED_IDS.FUNDED_DEAL_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    title: 'Tampa Ground-Up Construction',
    propertyPhotos: ['/assets/customers/coastal-funding/deals/deal-2.jpg'],
    propertyType: 'Single Family (Ground-Up)',
    city: 'Tampa',
    state: 'FL',
    loanType: 'Ground-Up Construction',
    loanAmount: 1200000,
    ltv: 80,
    fundingTimelineDays: 14,
    dealHighlights: [
      '3,200 sqft custom home',
      'Structured draw schedule',
      'Experienced builder',
      'Completed on time and on budget',
    ],
    borrowerTestimonial:
      'The draw process was seamless. Inspections were quick and funds hit my account within 48 hours.',
    closingDate: new Date('2025-12-03'),
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 2,
  },
  {
    id: WEBSITE_SEED_IDS.FUNDED_DEAL_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    title: 'Fort Lauderdale Bridge Loan',
    propertyPhotos: ['/assets/customers/coastal-funding/deals/deal-3.jpg'],
    propertyType: 'Townhouse',
    city: 'Fort Lauderdale',
    state: 'FL',
    loanType: 'Bridge',
    loanAmount: 680000,
    ltv: 75,
    fundingTimelineDays: 7,
    dealHighlights: ['Acquisition bridge', 'Interest-only payments', '12-month term', 'Closed in 7 days'],
    borrowerTestimonial:
      'When my conventional lender fell through, Coastal stepped in and saved the deal. Funded in one week.',
    closingDate: new Date('2025-12-20'),
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 3,
  },
  {
    id: WEBSITE_SEED_IDS.FUNDED_DEAL_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    title: 'Orlando DSCR Portfolio',
    propertyPhotos: ['/assets/customers/coastal-funding/deals/deal-4.jpg'],
    propertyType: 'Duplex',
    city: 'Orlando',
    state: 'FL',
    loanType: 'DSCR Bridge Plus',
    loanAmount: 950000,
    ltv: 70,
    fundingTimelineDays: 10,
    dealHighlights: ['No income docs required', 'DSCR 1.35', 'Cash-flow positive day one', 'Portfolio expansion'],
    borrowerTestimonial: null,
    closingDate: new Date('2026-01-10'),
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 4,
  },
  {
    id: WEBSITE_SEED_IDS.FUNDED_DEAL_5,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    title: 'Jacksonville Mixed-Use Bridge',
    propertyPhotos: ['/assets/customers/coastal-funding/deals/deal-5.jpg'],
    propertyType: 'Mixed-Use (Retail + 4 Units)',
    city: 'Jacksonville',
    state: 'FL',
    loanType: 'Bridge',
    loanAmount: 1800000,
    ltv: 65,
    fundingTimelineDays: 9,
    dealHighlights: ['Value-add acquisition', '$1.8M bridge financing', 'Lease-up in progress', '24-month term'],
    borrowerTestimonial: null,
    closingDate: new Date('2026-01-25'),
    status: 'PUBLISHED',
    featured: false,
    sortOrder: 5,
  },
];

// =============================================================================
// TESTIMONIALS (seeded in CRM Service)
// =============================================================================

export interface TestimonialSeed {
  id: string;
  businessId: string;
  authorName: string;
  authorTitle: string | null;
  authorCompany: string | null;
  content: string;
  rating: number;
  relatedDealId: string | null;
  status: string;
  featured: boolean;
  sortOrder: number;
}

export const COASTAL_FUNDING_TESTIMONIALS: TestimonialSeed[] = [
  {
    id: WEBSITE_SEED_IDS.TESTIMONIAL_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    authorName: 'Marcus R.',
    authorTitle: 'Fix & Flip Investor',
    authorCompany: null,
    content:
      'Coastal closed my first three deals in under 10 days each. Their team understood my renovation plan and got it done. I have been a repeat borrower ever since.',
    rating: 5,
    relatedDealId: WEBSITE_SEED_IDS.FUNDED_DEAL_1,
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 1,
  },
  {
    id: WEBSITE_SEED_IDS.TESTIMONIAL_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    authorName: 'Jennifer O.',
    authorTitle: 'Developer',
    authorCompany: null,
    content:
      'The draw process on my ground-up build was seamless. Inspections were quick and funds hit my account within 48 hours. Best construction lender I have worked with.',
    rating: 5,
    relatedDealId: WEBSITE_SEED_IDS.FUNDED_DEAL_2,
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 2,
  },
  {
    id: WEBSITE_SEED_IDS.TESTIMONIAL_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    authorName: 'David C.',
    authorTitle: 'Real Estate Investor',
    authorCompany: null,
    content:
      'When my conventional lender fell through two weeks before closing, Coastal stepped in and funded in 5 days. They saved my deal and have been my go-to lender ever since.',
    rating: 5,
    relatedDealId: WEBSITE_SEED_IDS.FUNDED_DEAL_3,
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 3,
  },
  {
    id: WEBSITE_SEED_IDS.TESTIMONIAL_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    authorName: 'Rachel T.',
    authorTitle: 'Portfolio Investor',
    authorCompany: null,
    content:
      'As a DSCR borrower, I appreciated not having to provide income documentation. Coastal made the process painless and the terms were investor-friendly.',
    rating: 5,
    relatedDealId: null,
    status: 'PUBLISHED',
    featured: true,
    sortOrder: 4,
  },
];

// =============================================================================
// BLOG POSTS (seeded in CRM Service)
// =============================================================================

export interface BlogPostSeed {
  id: string;
  businessId: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  authorName: string;
  category: string;
  tags: string[];
  metaTitle: string;
  metaDescription: string;
  status: string;
  publishedAt: Date;
}

export const COASTAL_FUNDING_BLOG_POSTS: BlogPostSeed[] = [
  {
    id: WEBSITE_SEED_IDS.BLOG_POST_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    slug: 'florida-fix-flip-market-outlook-2026',
    title: 'Florida Fix & Flip Market Outlook: What Investors Should Expect in 2026',
    excerpt:
      'With inventory levels rising and rates stabilizing, 2026 is shaping up to be a strong year for Florida fix & flip investors. Here is what you need to know.',
    content: `## The State of Fix & Flip in Florida (2026)

The Florida fix & flip market continues to evolve as we move into 2026. After a period of adjustment in 2024-2025, several key trends are creating favorable conditions for experienced investors.

### Rising Inventory Means More Opportunities

Housing inventory across Florida has increased steadily over the past 12 months, giving buyers more negotiating power and reducing competition at auction. This is particularly true in markets like Tampa Bay and Central Florida where new construction has added supply.

### Stabilizing Interest Rates

With the Fed signaling a pause in rate changes, private lending rates have stabilized in the 9-13% range. This predictability makes it easier to underwrite deals with confidence.

### Key Florida Markets to Watch

- **Miami-Dade** - Strong international demand continues to support high ARVs
- **Tampa Bay** - Population growth driving renovation demand in established neighborhoods
- **Jacksonville** - Affordable entry points with improving fundamentals
- **Orlando** - Vacation rental conversion opportunities near tourist corridors
- **Southwest Florida** - Post-hurricane rebuilding creating unique flip opportunities

### Our Advice

Focus on deals with strong fundamentals: buy below market, budget conservatively, and have a clear exit strategy. The best fix & flip deals in 2026 will be the ones with realistic ARVs and manageable renovation scopes.

*Ready to fund your next flip? [Contact our team](/contact) for a quote.*`,
    authorName: 'David Diaz',
    category: 'Market Insights',
    tags: ['fix-and-flip', 'market-outlook', '2026', 'florida', 'investing'],
    metaTitle: 'Florida Fix & Flip Market Outlook 2026',
    metaDescription:
      'Expert analysis of the 2026 Florida fix & flip market. Rising inventory, stabilizing rates, and key markets to watch for real estate investors.',
    status: 'PUBLISHED',
    publishedAt: new Date('2026-01-15'),
  },
  {
    id: WEBSITE_SEED_IDS.BLOG_POST_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    slug: 'ground-up-construction-financing-guide',
    title: 'Ground-Up Construction Financing: A Complete Guide for Florida Builders',
    excerpt:
      'Everything you need to know about financing ground-up construction in Florida, from draw schedules to builder qualifications and project feasibility.',
    content: `## What is Ground-Up Construction Financing?

Ground-up construction loans provide capital for building new residential or commercial properties from scratch. Unlike fix & flip loans that finance renovations to existing structures, construction loans fund the entire build process through a structured draw schedule.

### How Construction Draws Work

Construction financing is disbursed in stages tied to project milestones:

1. **Land Acquisition / Closing** - Initial funding for land purchase and closing costs
2. **Foundation & Framing** - First draw after foundation is poured and framing begins
3. **Rough-In** - Electrical, plumbing, HVAC rough-in completion
4. **Drywall & Finishes** - Interior finishing, cabinets, fixtures
5. **Final Completion** - Landscaping, final inspections, certificate of occupancy

Each draw requires an inspection to verify work completion. At Coastal, we fund draws within 48 hours of inspection approval.

### Builder Qualifications

Most construction lenders require:

- **Experience** - At least 2 completed ground-up projects of similar scale
- **Licensing** - Active general contractor license in Florida
- **Insurance** - Builder's risk, general liability, and workers' compensation
- **Financial Capacity** - Ability to fund initial costs before first draw

### Florida-Specific Considerations

- **Hurricane codes** - Florida building codes require wind mitigation, impact windows, and reinforced construction
- **Permitting timelines** - Vary significantly by county; Miami-Dade is typically 60-90 days, smaller counties can be faster
- **Insurance costs** - Factor in Florida's elevated insurance market when underwriting project feasibility
- **Flood zones** - Many Florida properties require elevation certificates and flood insurance

### Key Metrics We Evaluate

| Metric | What We Look For |
|--------|-----------------|
| Loan-to-Cost (LTC) | Up to 85% of total project cost |
| Builder Experience | 2+ completed projects |
| Project Feasibility | Realistic budget, timeline, and exit strategy |
| Market Demand | Strong comparable sales in the area |

*Interested in construction financing? [Get pre-qualified](/contact) with Coastal Real Estate Funding.*`,
    authorName: 'Roger Lehman',
    category: 'Education',
    tags: ['construction', 'ground-up', 'florida', 'builder-guide', 'draws'],
    metaTitle: 'Ground-Up Construction Financing Guide',
    metaDescription:
      'Complete guide to ground-up construction financing in Florida. Learn about draw schedules, builder qualifications, and Florida-specific requirements.',
    status: 'PUBLISHED',
    publishedAt: new Date('2026-01-28'),
  },
  {
    id: WEBSITE_SEED_IDS.BLOG_POST_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    slug: '5-mistakes-first-time-flippers-make',
    title: '5 Costly Mistakes First-Time Flippers Make (And How to Avoid Them)',
    excerpt:
      'New to fix & flip investing? Avoid these common pitfalls that cost first-time flippers thousands of dollars.',
    content: `## Learning from Others' Mistakes

Fix & flip investing can be highly profitable, but first-time investors often make costly mistakes that eat into their margins. Here are the five most common pitfalls and how to avoid them.

### 1. Overestimating the ARV

The After Repair Value (ARV) is the most important number in your deal analysis. First-time flippers often use optimistic comps or fail to account for market shifts during the renovation period.

**How to avoid it:** Use conservative comps from the last 90 days. Get a professional BPO or appraisal before you buy. In Florida markets, pay special attention to seasonal demand patterns.

### 2. Underestimating Renovation Costs

Budget overruns are the number one profit killer. First-time flippers often forget about permits, holding costs, utilities, and the inevitable surprises behind the walls.

**How to avoid it:** Add a 15-20% contingency to every budget. Get contractor bids in writing before closing. In Florida, factor in hurricane-code compliance costs for older properties.

### 3. Choosing the Wrong Contractor

A bad contractor will cost you time and money. Delays mean extra months of holding costs, and poor work means rework.

**How to avoid it:** Get referrals from other investors. Check references and verify licensing with the Florida Department of Business and Professional Regulation. Never pay more than 30% upfront.

### 4. Ignoring Holding Costs

Every month you hold a property costs money: loan interest, insurance, taxes, utilities, and maintenance. A 6-month flip that takes 12 months can turn a $50K profit into a $10K profit.

**How to avoid it:** Build holding costs into your deal analysis from day one. Work backward from your target sale date. Florida property insurance costs have risen significantly, so get quotes early.

### 5. Not Having a Backup Exit Strategy

What if the property does not sell at your target price? First-time flippers often have no Plan B.

**How to avoid it:** Before you buy, know your backup plan. Can you rent it? Can you sell at a lower price and still break even? DSCR refinance is a common backup. In Florida's strong rental market, this is often a viable option.

*Ready for your first (or next) flip? [Talk to our team](/contact) about financing options.*`,
    authorName: 'David Diaz',
    category: 'Education',
    tags: ['fix-and-flip', 'beginner', 'tips', 'mistakes-to-avoid', 'florida'],
    metaTitle: '5 Mistakes First-Time Flippers Make',
    metaDescription:
      'Avoid these 5 costly mistakes that first-time fix & flip investors make. Expert tips from Coastal Real Estate Funding on budgeting, contractors, and exit strategies.',
    status: 'PUBLISHED',
    publishedAt: new Date('2026-02-05'),
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getWebsiteDomainMapping(): WebsiteDomainMappingSeed {
  return DEMO_DOMAIN_MAPPING;
}

export function getWebsiteFundedDeals(): FundedDealSeed[] {
  return COASTAL_FUNDING_FUNDED_DEALS;
}

export function getWebsiteTestimonials(): TestimonialSeed[] {
  return COASTAL_FUNDING_TESTIMONIALS;
}

export function getWebsiteBlogPosts(): BlogPostSeed[] {
  return COASTAL_FUNDING_BLOG_POSTS;
}

// =============================================================================
// V4 CMS — BRAND PRESETS + COASTAL DEFAULT CONTENT (Design handoff v3, B0)
// =============================================================================
//
// New v4 content blocks added to `WebsiteSiteConfig.config` JSONB. These
// types live in `1-shared-types/src/schemas/website-cms.ts` and are validated
// at module load time so a broken preset surfaces at build, not at runtime.
//
// Brand presets mirror `BRANDS.{coastal,summit,beacon}` in
// `.claude/design-handoff-v3/project/pages/whitelabel.jsx` exactly. National
// positioning: eyebrow copy does not anchor any brand geographically — see
// `.claude/design-handoff-v3/chats/chat1.md` (2026-05-13) for the decision.

/**
 * The three launch brand presets — Coastal (teal/navy), Summit (copper),
 * Beacon (sunset orange). Each preset is a `BrandConfig`; tenants opt in by
 * writing the preset into `WebsiteSiteConfig.config.brand` via surgical
 * `jsonb_set` (see `.claude/rules/data-migrations.md`).
 */
export const BRAND_PRESETS = {
  coastal: {
    id: 'coastal',
    name: 'COASTAL',
    sub: 'FUNDING',
    fullName: 'Coastal Real Estate Funding',
    location: 'Ft. Myers, FL',
    tagline: 'Capital that understands your project.',
    italic: 'understands',
    eyebrow: 'PRIVATE REAL ESTATE CAPITAL · NATIONWIDE · $500K – $10M',
    accent: '#0d8a7a',
    accentDark: '#075b4f',
    accentSoft: '#dcf2ee',
    accentBorder: '#a8e0d6',
    ink: '#0b1929',
    bg: '#fafbfc',
    bgWarm: '#f6f7f4',
    bgSand: '#f4ede0',
    sand: '#c9a878',
    phone: '(239) 259-9829',
    domain: 'coastal-funding.com',
    body: "We've built 300 homes, completed 1,200 renovations, and underwritten $2B in real estate debt — before we wrote a single loan.",
  },
  summit: {
    id: 'summit',
    name: 'SUMMIT',
    sub: 'CAPITAL',
    fullName: 'Summit Capital Partners',
    location: 'Denver, CO',
    tagline: 'Lending engineered for builders.',
    italic: 'engineered',
    eyebrow: 'PRIVATE LENDING · ESTABLISHED 2019 · DENVER, CO',
    accent: '#c4783a',
    accentDark: '#8e5621',
    accentSoft: '#fbeede',
    accentBorder: '#e8c79b',
    ink: '#1c1c1e',
    bg: '#fbfaf7',
    bgWarm: '#f4f0ea',
    bgSand: '#ebe6dc',
    sand: '#9e8f70',
    phone: '(303) 555-0142',
    domain: 'summitcapital.co',
    body: 'Twenty years of structuring $4B+ in commercial real estate debt. We were operators first — we lend like it.',
  },
  beacon: {
    id: 'beacon',
    name: 'BEACON',
    sub: 'LENDING',
    fullName: 'Beacon Lending Group',
    location: 'Austin, TX',
    tagline: 'Direct capital for serious investors.',
    italic: 'serious',
    eyebrow: 'PRIVATE LENDING · TEXAS-BASED · TEXAS-FOCUSED',
    accent: '#d9603b',
    accentDark: '#a44119',
    accentSoft: '#fde6dc',
    accentBorder: '#f0b399',
    ink: '#0d2818',
    bg: '#f7faf7',
    bgWarm: '#eef2ed',
    bgSand: '#e6dfd0',
    sand: '#b5a586',
    phone: '(512) 555-0188',
    domain: 'beaconlending.com',
    body: '$1.8B funded across the Lone Star State. Texas-born, Texas-built, Texas-focused. We know the markets we lend in.',
  },
} as const satisfies Record<string, BrandConfig>;

/**
 * Default v4 CMS content for the Coastal tenant. Copy is national-positioning
 * (no geographic anchors in marketing surfaces); HQ address stays in the
 * footer separately. Mirrors the artboard content in
 * `.claude/design-handoff-v3/project/pages/coastal-v4.jsx` and
 * `coastal-v4-pages.jsx` while keeping numbers / names plausible for a
 * staging seed.
 *
 * Tenant-service writes this content into `WebsiteSiteConfig.config` via
 * surgical `jsonb_set` on the migration `20260517_seed_coastal_v4_website_config.ts`.
 */
export const coastalV4DefaultContent: WebsiteCmsV4 = {
  brand: BRAND_PRESETS.coastal,
  fundedDeals: [
    {
      slug: 'crf-2026-142',
      headline: 'Oakland fix & flip · 7 days to wire',
      amount: 1_300_000,
      ltv: 0.7,
      daysToClose: 7,
      closedAt: '2026-05-13',
      propertyType: 'fix-flip',
      photo: null,
      summary:
        '3bd / 2ba SFR, 1,640 sqft. Funded $725k purchase + $145k rehab in seven days from inquiry to wire. Borrower is a repeat operator with 12 closed flips.',
    },
    {
      slug: 'crf-2026-139',
      headline: 'Miami Beach bridge · 8-unit value-add',
      amount: 2_850_000,
      ltv: 0.65,
      daysToClose: 9,
      closedAt: '2026-05-11',
      propertyType: 'bridge',
      photo: null,
      summary:
        '8-unit multifamily M2M acquisition. 12-month bridge while the borrower seasons rents to qualify for agency take-out. No prepay.',
    },
    {
      slug: 'crf-2026-137',
      headline: 'Naples ground-up · 6-lot subdivision',
      amount: 4_100_000,
      ltv: 0.68,
      daysToClose: 14,
      closedAt: '2026-05-09',
      propertyType: 'construction',
      photo: null,
      summary:
        '6-lot residential subdivision. Staged construction draws against milestone inspections. Lot acquisition + 100% of vertical cost on an 18-month term.',
    },
  ],
  leadership: [
    {
      slug: 'david-diaz',
      name: 'David Diaz',
      title: 'Co-Founder · CEO',
      bio: 'David brings more than two decades of experience in real estate finance, construction, and institutional investment. He has been directly involved in over 36,000 single-family and multifamily units, completed more than 1,200 renovations, built over 300 new homes, and transacted on more than 4,000 residential lots. David began his career in commercial mortgage banking, where he underwrote and marketed more than $2 billion in structured real estate debt for agency, life company, and conduit lenders.',
      photo: null,
    },
    {
      slug: 'roger-lehman',
      name: 'Roger Lehman',
      title: 'Co-Founder · COO',
      bio: 'Roger brings over a decade of experience across capital markets, real estate finance, and development. He began his career on Wall Street working in equity and credit derivatives before transitioning into real estate investment and private lending. At Austray International, Lendmara, and Bluenest Development, he helped structure residential and commercial credit products while managing investor relations across diverse capital stacks.',
      photo: null,
    },
  ],
  // Six featured-market regions matching the v4 `CoastalMarketsV4` 2×3 grid
  // (`.claude/design-handoff-v3/project/pages/coastal-v4-pages.jsx`
  // lines 440-446). Each region carries the optional `markets` /
  // `counties` mono-caption rows and an accent CTA; `stats` is omitted
  // for sub-regions (no per-sub-region funded-totals exist).
  markets: [
    {
      slug: 'south-florida',
      name: 'South Florida',
      summary:
        'A dynamic and high-growth region known for its luxury real estate, vibrant tourism, and strong international investment. Ideal for high-value fix & flip and new construction projects.',
      markets: 'Miami · Fort Lauderdale · West Palm Beach',
      counties: 'Miami-Dade · Broward · Palm Beach',
    },
    {
      slug: 'central-florida',
      name: 'Central Florida',
      summary:
        'Home to major theme parks and a rapidly expanding population. Central Florida offers robust opportunities in residential development, vacation rentals, and commercial properties.',
      markets: 'Orlando · Kissimmee · Lakeland',
      counties: 'Orange · Osceola · Polk · Seminole',
    },
    {
      slug: 'tampa-bay',
      name: 'Tampa Bay',
      summary:
        'A diverse economic hub with a strong job market, beautiful coastlines, and a growing tech sector. Excellent for both residential and commercial bridge loans and new developments.',
      markets: 'Tampa · St. Petersburg · Clearwater',
      counties: 'Hillsborough · Pinellas · Pasco',
    },
    {
      slug: 'northeast-florida',
      name: 'Northeast Florida',
      summary:
        'Characterized by its expanding urban centers, growing port economy, and diverse housing demand. Strong investor demand in single-family rentals and ground-up construction.',
      markets: 'Jacksonville · St. Augustine',
      counties: 'Duval · St. Johns · Clay',
    },
    {
      slug: 'southwest-florida',
      name: 'Southwest Florida',
      summary:
        'A popular destination for retirees and second-home buyers. Strong demand for high-end residential renovation, new construction, and short-term rentals. Our home market.',
      markets: 'Naples · Fort Myers · Sarasota',
      counties: 'Collier · Lee · Sarasota',
    },
    {
      slug: 'nationwide',
      name: 'Nationwide',
      summary:
        'Beyond our featured Florida markets, we lend on residential and small-commercial real estate across all 50 states for qualified borrowers and well-structured deals.',
      markets: 'All 50 U.S. States',
      counties: 'Coast to coast coverage',
    },
  ],
  partners: {
    slug: 'broker-program',
    headline: 'Refer borrowers. Earn alongside us.',
    sub: 'Real-time attribution, transparent commissions, per-employee referral links. Send us your borrowers, we send back ours — every dollar attributed to the right person on both sides.',
    benefits: [
      '1.0% – 1.5% of loan amount on every funded deal',
      'Paid within 30 days of funding — no volume minimums',
      'Per-employee referral links with automatic attribution',
      'Inbound referrals from our pipeline when a deal fits your box better',
      'Quarterly partner reporting with funded volume and pipeline visibility',
    ],
    requirements: [
      'Established broker, originator, or real-estate professional',
      'Active book of investor-borrowers or repeat operators',
      'Signed partner agreement (we e-sign within 48 hours of approval)',
      'Compliance with state-by-state licensing requirements',
    ],
    // "Every employee gets their own link" sub-section per
    // `pages/coastal-v4-pages.jsx` lines 578-601. Sample rows are
    // privacy-preserving (last-initial), showcase real referral-link
    // shape, and act as a teaser for the per-employee attribution
    // narrative.
    attribution: {
      eyebrow: 'ATTRIBUTION',
      headline: 'Every employee gets',
      italic: 'their own link.',
      body: 'Each member of your team gets a custom referral link. Borrowers who apply through it are tagged to that employee — commissions go to the right person automatically. No manual reconciliation, no missing credit.',
      sampleLabel: 'YOUR TEAM · SAMPLE',
      sample: [
        { name: 'Daniel R.', role: 'Sr. Originator', link: 'coastal-funding.com/r/dr', conv: 12, earn: '$24.1k' },
        { name: 'Hannah P.', role: 'Originator', link: 'coastal-funding.com/r/hp', conv: 7, earn: '$11.8k' },
        { name: 'Theo P.', role: 'Principal', link: 'coastal-funding.com/r/tp', conv: 4, earn: '$8.6k' },
      ],
    },
    applyCta: 'Apply to partner',
  },
  intakeForm: {
    mode: 'page-by-page',
    theme: 'light',
    productList: ['fix-flip', 'construction', 'bridge', 'dscr'],
    sections: [
      {
        id: 'loan-type',
        title: 'Loan type',
        sub: 'What kind of financing are you looking for?',
        fields: [
          {
            id: 'loanType',
            kind: 'radio',
            label: 'Loan product',
            required: true,
            options: [
              {
                value: 'fix-flip',
                label: 'Fix & Flip',
                sub: 'Purchase, renovate, and resell residential properties (1–4 units)',
                recommended: true,
              },
              {
                value: 'construction',
                label: 'Ground-Up Construction',
                sub: 'Build new 1–4 unit residential properties from the ground up',
              },
              {
                value: 'bridge',
                label: 'Bridge',
                sub: 'Short-term capital for acquisitions, refinances, and cash-out on stabilized properties',
              },
              {
                value: 'dscr',
                label: 'DSCR / Rental',
                sub: 'Investment property loans qualified on rental income — no income docs',
              },
            ],
          },
        ],
      },
      {
        id: 'deal-financials',
        title: 'Deal financials',
        sub: 'Walk us through the economics.',
        fields: [
          {
            id: 'purchasePrice',
            kind: 'number',
            label: 'Purchase price',
            prefix: '$',
            required: true,
            min: 0,
          },
          {
            id: 'rehabBudget',
            kind: 'number',
            label: 'Rehab budget',
            prefix: '$',
            hint: 'All-in including soft costs',
            required: true,
            min: 0,
          },
          {
            id: 'arv',
            kind: 'number',
            label: 'After-repair value (ARV)',
            prefix: '$',
            required: true,
            min: 0,
          },
          {
            id: 'asIsValue',
            kind: 'number',
            label: 'As-is value (today)',
            prefix: '$',
            placeholder: '0',
            min: 0,
          },
          {
            id: 'exitStrategy',
            kind: 'text',
            label: 'Exit strategy',
            placeholder: 'Sale within 12 months',
            required: true,
            maxLength: 280,
          },
        ],
      },
      {
        id: 'borrower-info',
        title: 'Borrower information',
        sub: 'Tell us about your entity and experience.',
        fields: [
          {
            id: 'fullName',
            kind: 'text',
            label: 'Full legal name',
            required: true,
            maxLength: 200,
          },
          {
            id: 'email',
            kind: 'text',
            label: 'Email',
            required: true,
            pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
            maxLength: 320,
          },
          {
            id: 'entityType',
            kind: 'select',
            label: 'Borrowing entity',
            required: true,
            options: [
              { value: 'llc', label: 'LLC' },
              { value: 'corporation', label: 'Corporation' },
              { value: 'partnership', label: 'Partnership' },
              { value: 'individual', label: 'Individual' },
            ],
          },
          {
            id: 'priorDealsCompleted',
            kind: 'number',
            label: 'Prior deals completed',
            suffix: 'deals',
            min: 0,
            max: 1000,
          },
        ],
      },
      {
        id: 'review',
        title: 'Review & submit',
        sub: 'One last look before we send your file to underwriting.',
        fields: [
          {
            id: 'supportingDocs',
            kind: 'file',
            label: 'Supporting docs (optional)',
            hint: 'P&L, rent roll, contractor scope, photos — anything that strengthens your file.',
            multiple: true,
            maxSizeBytes: 25_000_000,
            accept: ['application/pdf', 'image/*', '.docx', '.xlsx'],
          },
        ],
      },
    ],
  },
};

// Module-load validation — fail loud at build time if the seed drifts from
// the schema, instead of shipping invalid CMS content and failing at runtime
// when a render path first reads the value.
WebsiteCmsV4Schema.parse(coastalV4DefaultContent);
